// Landing-page + dashboard i18n dictionary (EN default, IT, DE for public site).
// Dashboard keys remain in EN for the authenticated Client Portal (Phase 1 scope).
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import ES_PUBLIC from "@/locales/es.public.json";
import ZH_PUBLIC from "@/locales/zh.public.json";
import RU_PUBLIC from "@/locales/ru.public.json";
import ES_WORKSPACE from "@/locales/es.workspace.json";
import ZH_WORKSPACE from "@/locales/zh.workspace.json";
import RU_WORKSPACE from "@/locales/ru.workspace.json";
import IT_WORKSPACE_MISSING from "@/locales/it.workspace-missing.json";
import DE_WORKSPACE_MISSING from "@/locales/de.workspace-missing.json";
import { CONVERSION_LOCALES } from "@/locales/conversion";

const EN = {
  // ============ Public Header ============
  nav_product: "Product",
  nav_documents: "Documents",
  nav_how: "How It Works",
  nav_use_cases: "Use Cases",
  nav_pricing: "Pricing",
  nav_about: "About",
  nav_contact: "Contact",
  nav_login: "Log In",
  nav_join: "Create 3 Documents Free",
  nav_workspace: "My Workspace",
  nav_logout: "Sign Out",
  nav_demo: "Watch Demo",

  // ============ Hero ============
  hero_label: "TRADEDOCS AI DESK",
  hero_title: "Professional documents for international transactions — prepared through one intelligent workspace.",
  hero_deal_line: "Buyer and seller. One private deal room.",
  hero_sub: "Create trade documents with AI, share them with your counterparty and discuss revisions in a private workspace.",
  hero_cta_primary: "Create 3 Documents Free",
  hero_cta_secondary: "Explore the Platform",
  hero_cta_login: "Log In",
  trust_1: "International trade workflows",
  trust_2: "English, Italian, German, Spanish, Chinese and Russian",
  trust_3: "Editable document output",
  trust_4: "Confidential handling",
  trust_5: "Optional expert review",
  hero_mockup_caption: "From structured transaction data to an editable professional document.",

  // ============ Product Intro ============
  product_label: "THE PRODUCT",
  product_title: "More than a template library.",
  product_sub: "TradeDocs combines structured templates, transaction data and AI-assisted drafting inside one professional document workspace.",
  product_card_1_title: "Structured Templates",
  product_card_1_desc: "Templates designed for real international trade and shipping workflows.",
  product_card_2_title: "Deal-Based Drafting",
  product_card_2_desc: "Documents generated using the specific parameters of the client's transaction.",
  product_card_3_title: "Document Review",
  product_card_3_desc: "Key terms, missing information and potential inconsistencies highlighted before use.",
  product_card_4_title: "Reusable Business Data",
  product_card_4_desc: "Company details, counterparties and transaction information saved for future documents.",

  // ============ Three Ways ============
  three_ways_title: "Start in the way that works for you.",
  three_ways_1_title: "Use a Template",
  three_ways_1_desc: "Select a professional template, enter the transaction parameters and generate a structured draft.",
  three_ways_1_cta: "Browse Templates",
  three_ways_2_title: "Upload a Document",
  three_ways_2_desc: "Upload an existing document to summarise it, identify missing information or prepare a revised version.",
  three_ways_2_cta: "Review a Document",
  three_ways_3_title: "Describe What You Need",
  three_ways_3_desc: "Explain your commercial objective and let TradeDocs recommend the most suitable document or workflow.",
  three_ways_3_cta: "Describe Your Request",
  three_ways_3_badge: "Easiest entry",

  // ============ Document Categories ============
  cats_title: "Documents for real international transactions.",
  cats_view: "View Templates",
  cat_commercial: "Commercial Documents",
  cat_commercial_desc: "Opening papers, corporate offers and business correspondence.",
  cat_trade_finance: "Trade Finance",
  cat_trade_finance_desc: "Bank letters, documentary credits and readiness reviews.",
  cat_shipping: "Shipping & Chartering",
  cat_shipping_desc: "Chartering exchanges, laytime, claims and vessel data.",
  cat_corporate: "Corporate & Due Diligence",
  cat_corporate_desc: "KYC, DD questionnaires and corporate declarations.",
  cat_contracts: "Contracts & Formal Correspondence",
  cat_contracts_desc: "Supply agreements, notices, amendments and cover letters.",
  cat_management: "Management Briefs",
  cat_management_desc: "Executive summaries and practical next-step recommendations.",

  // ============ Platform Preview ============
  preview_label: "INSIDE TRADEDOCS",
  preview_title: "One workspace for your documents and transactions.",
  preview_step_1: "Select a template",
  preview_step_2: "Add transaction parameters",
  preview_step_3: "Upload supporting documents",
  preview_step_4: "Generate a professional draft",
  preview_step_5: "Review missing information",
  preview_step_6: "Export the document",

  // ============ How It Works ============
  how_title: "From transaction data to a completed document.",
  how_1_t: "Choose",
  how_1_d: "Select a template, upload a file or describe the required outcome.",
  how_2_t: "Add Information",
  how_2_d: "Enter the parties, product, quantity, price, Incoterms and other relevant details.",
  how_3_t: "Generate",
  how_3_d: "TradeDocs structures the information and prepares a professional draft.",
  how_4_t: "Review",
  how_4_d: "Check missing details, suggestions and potential inconsistencies.",
  how_5_t: "Export",
  how_5_d: "Save the document and export it in an editable format.",

  // ============ Use Cases ============
  uc_title: "Built for practical commercial work.",
  uc_1_t: "Commodity Trading",
  uc_1_d: "Prepare an LOI, commercial offer, due-diligence questionnaire and follow-up correspondence.",
  uc_2_t: "Shipping & Chartering",
  uc_2_d: "Prepare chartering questions, recaps, claims and owner-charterer correspondence.",
  uc_3_t: "Trade Finance",
  uc_3_d: "Structure BCL, SBLC and LC drafts together with documentary checklists.",
  uc_4_t: "Cross-Border Supply",
  uc_4_d: "Prepare proforma invoices, supply agreements, commercial offers and formal correspondence.",
  uc_5_t: "Counterparty Due Diligence",
  uc_5_d: "Generate structured KYC and due-diligence requests based on the transaction type.",
  uc_6_t: "Management Reporting",
  uc_6_d: "Convert a complex document package into a concise management summary and action list.",

  // ============ AI Drafting Callout ============
  ai_title: "AI-assisted drafting with transaction-focused logic.",
  ai_sub: "TradeDocs helps structure information, prepare drafts and identify missing details. The platform is designed around international trade and shipping workflows rather than generic text generation.",
  ai_b_1: "Faster first drafts",
  ai_b_2: "Consistent document structure",
  ai_b_3: "Reusable company information",
  ai_b_4: "Multilingual output",
  ai_b_5: "Editable documents",
  ai_b_6: "Missing-information checks",
  ai_b_7: "Optional expert support",

  // ============ Professional Support ============
  ps_title: "Need professional support for a complex transaction?",
  ps_sub: "Human review and tailored assistance may be requested for complex shipping, trade finance, contractual or multi-party assignments.",
  ps_cta: "Request Expert Support",

  // ============ Confidentiality ============
  conf_title: "Designed for confidential business documents.",
  conf_1: "Controlled access",
  conf_2: "Protected user accounts",
  conf_3: "Confidential file handling",
  conf_4: "No public use of client documents",
  conf_5: "NDA available upon request",
  conf_6: "Document deletion options",
  conf_7: "Organisation-based data separation",
  conf_8: "Clear data retention information",

  // ============ Membership ============
  mem_label: "MEMBERSHIP",
  mem_title: "One membership for everyday international documentation.",
  mem_sub: "Access the TradeDocs template library, AI-assisted drafting tools, document workflows and saved transaction information through one monthly membership.",
  mem_area_1: "Template Library",
  mem_area_2: "AI-assisted drafting",
  mem_area_3: "Document review tools",
  mem_area_4: "Saved company profile",
  mem_area_5: "Saved counterparties",
  mem_area_6: "Transaction workspaces",
  mem_area_7: "Document history",
  mem_area_8: "Multilingual output",
  mem_area_9: "Editable exports",
  mem_note: "Create and export three complete documents without a payment card, then choose a monthly plan.",
  mem_expert: "Human expert review and bespoke transaction support may be ordered separately.",

  // ============ Early Access Form ============
  ea_title: "Prepare your first trade document today.",
  ea_sub: "Create a workspace, choose a professional template and export up to three complete documents without a payment card.",
  ea_first: "First name",
  ea_last: "Last name",
  ea_company: "Company",
  ea_email: "Business email",
  ea_country: "Country",
  ea_area: "Main area of work",
  ea_area_opt_1: "International Trade",
  ea_area_opt_2: "Commodity Trading",
  ea_area_opt_3: "Shipping",
  ea_area_opt_4: "Chartering",
  ea_area_opt_5: "Trade Finance",
  ea_area_opt_6: "Import and Export",
  ea_area_opt_7: "Corporate Services",
  ea_area_opt_8: "Other",
  ea_lang: "Preferred language",
  ea_doc: "Main document requirement",
  ea_message: "Optional message",
  ea_cta: "Create Free Account",
  ea_ok: "Thank you. Your early-access request has been received.",
  ea_err: "Something went wrong. Please try again.",
  ea_privacy: "By submitting you confirm that TradeDocs may contact you regarding your request. Confidential handling.",

  // ============ Alternative Request Form ============
  ar_title: "Have a document or transaction to prepare?",
  ar_sub: "Send a short brief and our desk will get back to you.",
  ar_name: "Name",
  ar_company: "Company",
  ar_email: "Email",
  ar_type: "Request type",
  ar_type_opt_1: "Commercial document",
  ar_type_opt_2: "Trade finance",
  ar_type_opt_3: "Shipping / Chartering",
  ar_type_opt_4: "Corporate / DD",
  ar_type_opt_5: "Other",
  ar_desc: "Brief description",
  ar_desc_ph: "Commodity, counterparty region, current stage, what you need from us.",
  ar_lang: "Preferred language",
  ar_urgency: "Urgency",
  ar_urgency_1: "Normal",
  ar_urgency_2: "High",
  ar_urgency_3: "Urgent",
  ar_cta: "Submit Request",
  ar_ok: "Thank you. We'll be in touch within one business day.",

  // ============ FAQ ============
  faq_title: "Frequently asked questions.",
  faq_q_1: "What is TradeDocs?",
  faq_a_1: "TradeDocs is a document workspace for international trade professionals. It combines structured templates, transaction data and AI-assisted drafting to prepare commercial, shipping and trade-finance documents faster and more consistently.",
  faq_q_2: "Is TradeDocs a law firm?",
  faq_a_2: "No. TradeDocs is a professional document platform. It is not a law firm and does not provide legal advice. All output is intended as a working draft that the user retains full control over.",
  faq_q_3: "Do I need to know which template to use?",
  faq_a_3: "Not necessarily. You can use a template you already know, upload an existing document, or simply describe what you need — TradeDocs will suggest the most suitable document or workflow.",
  faq_q_4: "Which document types are available?",
  faq_a_4: "Commercial documents (LOI, ICPO, offers), trade finance (BCL, LC, SBLC), shipping and chartering (recaps, laytime, claims), corporate & DD (KYC, DDQ), and contracts / formal correspondence.",
  faq_q_5: "Can I upload an existing document?",
  faq_a_5: "Yes. You can upload a document to summarise it, identify missing information or prepare a revised version.",
  faq_q_6: "Which languages are supported?",
  faq_a_6: "The public site and product interface are available in English, Italian and German. Documents can be generated and edited in multiple languages depending on the template.",
  faq_q_7: "Can I export documents to Word or PDF?",
  faq_a_7: "Yes. All drafts can be exported to editable Word (.docx) and PDF for immediate use or further editing outside TradeDocs.",
  faq_q_8: "Is human expert review included?",
  faq_a_8: "Automated review is included. Human expert review and bespoke transaction support may be ordered separately for complex assignments.",
  faq_q_9: "How is my information handled?",
  faq_a_9: "TradeDocs uses controlled access, protected user accounts and organisation-based data separation. Client documents are not used for any public purpose. NDA available upon request.",
  faq_q_10: "Can I try TradeDocs before subscribing?",
  faq_a_10: "Yes. A new organisation can create, edit and export three complete documents without a payment card. A monthly subscription is required for further document creation.",
  faq_q_11: "Can my company use TradeDocs as a team?",
  faq_a_11: "Yes. TradeDocs supports organisation accounts, shared templates and shared transaction workspaces. Team features are being expanded ahead of the public launch.",

  // ============ Footer ============
  ft_product: "Product",
  ft_docs: "Document Areas",
  ft_company: "Company",
  ft_legal: "Legal",
  ft_languages: "Languages",
  ft_link_overview: "Product Overview",
  ft_link_pricing: "Pricing",
  ft_link_templates: "Templates",
  ft_link_how: "How It Works",
  ft_link_use_cases: "Use Cases",
  ft_link_early: "Create Free Account",
  ft_link_login: "Log In",
  ft_link_about: "About TradeDocs",
  ft_link_contact: "Contact",
  ft_link_support: "Expert Support",
  ft_link_privacy: "Privacy Policy",
  ft_link_terms: "Terms of Service",
  ft_link_subscription: "Subscription & Cancellation",
  ft_link_cookies: "Cookie Policy",
  ft_link_dp: "Data Processing",
  ft_link_nda: "NDA Information",
  ft_rights: "All rights reserved.",
  ft_tagline: "AI-assisted document workspace for international trade, shipping and trade finance.",

  // ============ Client Portal (kept minimal — Phase 2 scope) ============
  d_nav_dashboard: "Dashboard",
  d_nav_demo: "Watch demo",
  d_nav_deals: "Deals",
  d_nav_tasks: "Action Center",
  d_nav_notifications: "Notifications",
  d_nav_studio: "Document Studio",
  d_nav_drafting: "Document Studio",
  d_nav_review: "Document Review",
  d_nav_templates: "Template Library",
  d_nav_files: "Files",
  d_nav_counterparties: "Counterparties",
  d_nav_company: "Company Profile",
  d_nav_expert: "Expert Support",
  d_nav_requests: "My Requests",
  d_nav_intakes: "My Requests",
  d_nav_billing: "Billing & Usage",
  d_nav_settings: "Settings",
  d_nav_organization: "Organisation & team",
  org_kicker: "Workspace administration",
  org_title: "Organisation & team",
  org_intro: "Manage your shared workspace, seats, invitations, roles and responsibility assignments.",
  org_details: "Organisation details",
  org_name: "Organisation name",
  org_seats: "Seats used",
  org_pending: "pending invitation(s)",
  org_invite_title: "Invite a team member",
  org_role: "Role",
  org_invite: "Send invitation",
  org_members: "Active members",
  org_pending_invites: "Pending invitations",
  org_incoming: "Your invitations",
  org_accept: "Join workspace",
  org_role_owner: "Owner",
  org_role_admin: "Administrator",
  org_role_manager: "Manager",
  org_role_reviewer: "Reviewer",
  org_role_member: "Member",
  org_saved: "Organisation updated",
  org_invited: "Invitation created",
  org_seat_limit: "All available seats are already assigned or reserved.",
  org_workspace_not_empty: "This account already has workspace data and cannot join automatically.",
  org_load_failed: "Could not load the organisation workspace.",
  org_remove: "Remove member",
  org_remove_confirm: "Remove this member from the organisation and free the seat?",
  org_removed: "Member removed and seat released",
  d_nav_logout: "Sign out",
  d_section_workspace: "Workspace",
  d_section_business: "Business Data",
  d_section_account: "Account",

  // ============ Account settings ============
  settings_intro: "Manage your personal profile, interface language and account security.",
  settings_profile: "Personal profile",
  settings_profile_help: "These details identify you inside your TradeDocs workspace.",
  settings_full_name: "Full name",
  settings_company: "Company",
  settings_email: "Email address",
  settings_email_help: "Contact support to change the email used to sign in.",
  settings_save_profile: "Save profile",
  settings_profile_saved: "Profile updated",
  settings_profile_failed: "Could not update profile",
  settings_language: "Interface language",
  settings_language_help: "This preference is saved in this browser.",
  settings_security: "Password and security",
  settings_security_help: "Use at least 12 characters and avoid passwords used on other services.",
  settings_current_password: "Current password",
  settings_new_password: "New password",
  settings_confirm_password: "Confirm new password",
  settings_change_password: "Change password",
  settings_password_changed: "Password changed",
  settings_password_failed: "Could not change password",
  settings_password_mismatch: "New passwords do not match",
  settings_future_security: "Two-factor authentication and session management are planned for a later release.",
  settings_notifications: "Email notifications",
  settings_notifications_help: "Choose which non-essential updates TradeDocs may send to your account email.",
  settings_deal_updates: "Deal activity and reminders",
  settings_deal_updates_help: "Status changes, upcoming actions and transaction reminders.",
  settings_document_updates: "Document activity",
  settings_document_updates_help: "Draft completion, review results and document workflow updates.",
  settings_product_updates: "Product news",
  settings_product_updates_help: "Occasional release notes and product announcements.",
  settings_security_alerts: "Essential security alerts remain enabled and cannot be switched off.",
  settings_save_notifications: "Save notification preferences",
  settings_notifications_saved: "Notification preferences updated",
  settings_notifications_failed: "Could not update notification preferences",
  settings_data: "Your data",
  settings_data_help: "Download a portable JSON copy of your account and workspace records.",
  settings_export: "Download my data",
  settings_exported: "Data export downloaded",
  settings_export_failed: "Could not export account data",

  // ============ Action Center ============
  tasks_title: "Action Center",
  tasks_intro: "Keep transaction follow-ups, document work and deadlines in one operational queue.",
  tasks_add_title: "Add an action",
  tasks_task: "Action",
  tasks_task_placeholder: "e.g. Confirm LC wording with issuing bank",
  tasks_due: "Due date",
  tasks_priority: "Priority",
  tasks_priority_low: "Low",
  tasks_priority_normal: "Normal",
  tasks_priority_high: "High",
  tasks_deal: "Related deal",
  tasks_assignee: "Responsible",
  tasks_unassigned: "Unassigned",
  tasks_no_deal: "No deal",
  tasks_add: "Add action",
  tasks_filter_open: "Open",
  tasks_filter_completed: "Completed",
  tasks_filter_all: "All",
  tasks_empty_open: "No open actions. Your operational queue is clear.",
  tasks_empty_completed: "No completed actions yet.",
  tasks_empty_all: "No actions yet.",
  tasks_overdue: "Overdue",
  tasks_due_today: "Due today",
  tasks_created: "Action added",
  tasks_create_failed: "Could not add action",
  tasks_update_failed: "Could not update action",
  tasks_delete_failed: "Could not delete action",
  tasks_delete_confirm: "Delete this action?",
  tasks_open_deal: "Open deal",
  tasks_status_open: "Open",
  tasks_status_completed: "Completed",
  db_action_title: "Action Center",
  db_action_empty: "No open actions.",
  db_action_view_all: "Open Action Center",

  // ============ Notification inbox ============
  notifications_title: "Notifications",
  notifications_intro: "A single operational feed for document work, review results, files, deal workflow and due actions.",
  notifications_read_all: "Mark all read",
  notifications_filter_unread: "Unread",
  notifications_filter_all: "All",
  notifications_empty_unread: "You are all caught up.",
  notifications_empty_all: "No activity notifications yet.",
  notifications_mark_read: "Mark as read",
  notifications_delete: "Delete notification",
  notifications_load_failed: "Could not load notifications",
  notifications_update_failed: "Could not update notification",
  notifications_delete_failed: "Could not delete notification",

  // ============ Private file library ============
  files_title: "Files & document sets",
  files_intro: "Keep source documents, evidence and transaction attachments in private sets linked to each deal.",
  files_upload_title: "Add a file",
  files_file: "File",
  files_deal: "Related deal",
  files_no_deal: "No deal",
  files_set: "Document set",
  files_set_placeholder: "e.g. LC presentation",
  files_type: "Document type",
  files_type_placeholder: "e.g. Bill of lading",
  files_upload: "Upload",
  files_uploading: "Uploading…",
  files_limit_hint: "PDF, Office, image, text and email files up to 25 MB. Files remain private to your account.",
  files_items: "files",
  files_search: "Search files or sets",
  files_unfiled: "Unfiled",
  files_empty: "No files yet",
  files_empty_hint: "Upload the first source document or transaction attachment.",
  files_uploaded: "File uploaded",
  files_upload_failed: "Could not upload file",
  files_load_failed: "Could not load files",
  files_update_failed: "Could not update file",
  files_deleted: "File deleted",
  files_delete_failed: "Could not delete file",
  files_delete_confirm: "Permanently delete this file?",
  files_rename_prompt: "File name",
  files_rename: "Rename file",
  files_download: "Download file",
  files_immutable: "Immutable version",
  files_delete: "Delete file",

  // ============ Structured document review ============
  review_title: "Document Review",
  review_intro: "Run an evidence-based pre-check, prioritise issues and track every finding through resolution.",
  review_new: "New review",
  review_source_upload: "Upload new",
  review_source_existing: "Use existing file",
  review_file: "Document",
  review_existing_file: "File library",
  review_choose_file: "Choose a file",
  review_deal: "Related deal",
  review_no_deal: "No deal",
  review_run: "Run review",
  review_processing: "Checking…",
  review_scope_hint: "Searchable documents are checked locally. PDF scans and images can also be read when you explicitly start structured extraction.",
  review_upload_set: "Review uploads",
  review_document_type: "Document for review",
  review_completed: "Review completed",
  review_failed: "Could not review document",
  review_load_failed: "Could not load reviews",
  review_update_failed: "Could not update finding",
  review_empty: "No review reports yet",
  review_empty_hint: "Upload a document or choose a file from your private library.",
  review_history: "Review history",
  review_status_attention_required: "Attention required",
  review_status_ready: "Ready",
  review_open_short: "open",
  review_report: "Review report",
  review_words: "Words",
  review_high: "High",
  review_medium: "Medium",
  review_low: "Low",
  review_open: "Open",
  review_summary: "Extracted summary",
  review_findings: "Structured findings",
  review_show_all: "Show all",
  review_show_open: "Show open only",
  review_no_open: "No open findings remain.",
  review_severity_high: "High",
  review_severity_medium: "Medium",
  review_severity_low: "Low",
  review_finding_resolved: "Resolved",
  review_finding_accepted: "Accepted",
  review_resolve: "Mark resolved",
  review_accept: "Accept risk",
  review_accept_short: "Accept",
  review_reopen: "Reopen finding",
  review_delete: "Delete review",
  review_delete_confirm: "Delete this review report? The source file will remain in Files.",
  review_deleted: "Review deleted",
  review_delete_failed: "Could not delete review",
  review_import_kicker: "Document to Deal Master",
  review_import_title: "Structured data import",
  review_import_intro: "Read a PDF, scan or photo and propose transaction fields. Nothing changes in the deal until you select and confirm individual fields.",
  review_import_extract: "Extract fields",
  review_import_extracting: "Reading document…",
  review_import_needs_deal: "This file is not linked to a deal. Link or upload it to a deal before importing transaction data.",
  review_import_ready: "Structured suggestions are ready for confirmation",
  review_import_empty: "No sufficiently supported transaction fields were found. The deal has not been changed.",
  review_import_failed: "Could not extract structured deal data",
  review_import_choose: "Choose the fields to import",
  review_import_applied_title: "Confirmed import record",
  review_import_confidence_high: "High confidence",
  review_import_confidence_medium: "Medium confidence",
  review_import_confidence_low: "Low confidence",
  review_import_confirm_hint: "Compare every value with the source document. Confirmed values update the Deal Master and its revision history.",
  review_import_apply: "Apply selected",
  review_import_applying: "Applying…",
  review_import_applied: "Confirmed fields added to the Deal Master",
  review_import_apply_failed: "Could not apply the selected fields",
  review_disclaimer: "Automated pre-check only. High-impact commercial, legal, banking and shipping documents should receive appropriate professional review.",

  // ============ Dashboard page ============
  db_welcome: "Welcome back",
  db_welcome_sub: "Continue an active transaction or start a new document.",
  db_qa_create: "Create a Document",
  db_qa_create_sub: "Start from a professional template.",
  db_qa_review: "Review a Document",
  db_qa_review_sub: "Upload and analyse an existing document.",
  db_qa_new_deal: "Start a New Deal",
  db_qa_new_deal_sub: "Open a fresh transaction workspace.",
  db_qa_describe: "Describe What You Need",
  db_qa_describe_sub: "Let TradeDocs recommend a workflow.",
  db_section_active: "Active deals",
  db_section_attention: "Documents requiring attention",
  db_section_recent: "Recent documents",
  db_section_membership: "Membership & usage",
  db_view_all: "View all",
  db_empty_deals: "No active deals — start your first one.",
  db_empty_attention: "Nothing needs attention right now.",
  db_empty_docs: "No documents yet.",
  db_open: "Open",
  db_plan_early: "Early Access",
  db_renews: "Renews",
  db_usage_docs: "Documents generated",
  db_usage_pages: "Pages processed",
  db_usage_deals: "Active deals",
  db_usage_storage: "Storage",
  db_coming_soon: "Coming soon",
  db_coming_soon_sub: "This area is being prepared for the next Client Portal release.",
  db_notify: "Notify me when available",
  d_deals_title: "Active deals",
  d_deals_new: "New deal",
  d_deals_empty: "No deals yet. Create your first one.",
  d_deals_kicker: "Workspace",
  d_deals_loading: "Loading…",
  d_col_title: "Title",
  d_col_counterparty: "Counterparty",
  d_col_type: "Line",
  d_col_stage: "Stage",
  d_col_status: "Status",
  d_col_updated: "Updated",
  d_stage_intake: "Intake",
  d_stage_drafting: "Drafting",
  d_stage_qa: "QA",
  d_stage_delivery: "Delivery",
  d_type_commercial: "Commercial Drafting",
  d_type_documentary: "Documentary Support",
  d_type_shipping: "Shipping & Chartering",
  d_type_management: "Management Desk",
  d_new_kicker: "New Deal",
  d_new_title: "Open a new deal",
  d_new_field_title: "Title",
  d_new_field_counterparty: "Counterparty",
  d_new_field_type: "Line",
  d_new_field_value: "Value",
  d_new_field_notes: "Notes",
  d_new_value_ph: "e.g. USD 2.5M",
  d_new_submit: "Create",
  d_new_close: "Close",
  d_toast_created: "Deal created",
  d_toast_create_failed: "Failed to create deal",
  d_toast_stage_updated: "Stage updated",
  d_toast_failed: "Operation failed",
  d_toast_deleted: "Deleted",
  d_deals_delete_action: "Delete",
  draft_title: "AI Drafting",
  draft_sub: "Generate a professional commercial or documentary draft in seconds.",
  draft_template: "Template",
  draft_language: "Language",
  draft_parameters: "Deal parameters",
  draft_param_key: "Field",
  draft_param_value: "Value",
  draft_add_param: "Add field",
  draft_generate: "Generate draft",
  draft_generating: "Drafting…",
  draft_output: "Draft output",
  draft_copy: "Copy",
  draft_copied: "Copied",
  tpl_title: "Template library",
  tpl_sub: "The internal core of the desk. Categorised, searchable, ready to use.",
  tpl_search: "Search templates…",
  tpl_cat_all: "All",
  tpl_cat_commercial: "Commercial",
  tpl_cat_documentary: "Documentary",
  tpl_cat_shipping: "Shipping",
  tpl_cat_management: "Management",
  tpl_use: "Use template",
  iq_title: "Intake queue",
  iq_empty: "No submissions yet.",
  iq_col_when: "When",
  iq_col_company: "Company",
  iq_col_contact: "Contact",
  iq_col_email: "Email",
  iq_col_type: "Line",
  iq_col_urgency: "Urgency",
  iq_col_brief: "Brief",
  iq_col_status: "Status",
  iq_status_new: "New",
  iq_status_contacted: "Contacted",
  iq_status_converted: "Converted",
  iq_status_archived: "Archived",
  dd_back: "Back to deals",
  dd_overview: "Overview",
  dd_drafts: "Drafts",
  dd_no_drafts: "No drafts yet for this deal.",
  dd_new_draft: "New draft for this deal",
  dd_export_docx: "Export .docx",
  dd_export_pdf: "Export .pdf",
  dd_delete_draft: "Delete",
  dd_meta_counterparty: "Counterparty",
  dd_meta_line: "Line",
  dd_meta_value: "Value",
  dd_meta_stage: "Stage",
  dd_meta_created: "Created",
  dd_notes: "Notes",
  dd_context: "Drafting for deal",
  dd_draft_title_ph: "Title (optional)",
  dd_draft_notes_ph: "Internal notes…",
  dd_save: "Save",
  dd_saved: "Saved",
  dd_edit: "Edit",
  dl_search_ph: "Search deals…",
  dl_sort_updated: "Recently updated",
  dl_sort_created: "Newest",
  dl_sort_title: "Title A→Z",
  dl_sort_stage: "Stage",
  dl_no_match: "No deals match your search.",
  auth_signin: "Sign in",
  auth_signup: "Create account",
  auth_email: "Email",
  auth_password: "Password",
  auth_name: "Full name",
  auth_company: "Company (optional)",
  auth_no_account: "No account?",
  auth_have_account: "Already registered?",
  auth_login_btn: "Sign in",
  auth_register_btn: "Register",
  auth_err_login: "Invalid email or password",
  auth_hero_title: "The external desk your deals deserve.",
  auth_hero_sub: "Sign in to your workspace — deals, drafts, and the template library.",
  auth_welcome_back: "Welcome back.",
  auth_open_desk: "Open your desk.",
  auth_trust_line: "Confidential · NDA · Encrypted",
  auth_loading: "…",
  auth_forgot_password: "Forgot your password?",
  auth_privacy_signup: "By creating an account, you acknowledge how TradeDocs processes your personal data:",
  auth_privacy_signin: "Information about personal-data processing:",

  // ============ Studio labels — Company Profile ============
  cp_intro: "Saved once, reused everywhere. Legal headers, signatories and bank details auto-fill into generated documents.",
  cp_sec_legal: "Legal identity",
  cp_sec_contact: "Contact",
  cp_sec_defaults: "Defaults",
  cp_sec_signatories: "Signatories",
  cp_sec_bank: "Bank & Footer",
  cp_f_legal_name: "Legal company name",
  cp_f_country: "Country",
  cp_f_registration: "Registration number",
  cp_f_vat: "VAT / Tax ID",
  cp_f_registered_addr: "Registered address",
  cp_f_operational_addr: "Operational address",
  cp_f_website: "Website",
  cp_f_phone: "Phone",
  cp_f_email: "Public email",
  cp_f_currency: "Default currency",
  cp_f_lang: "Default document language",
  cp_f_name: "Name",
  cp_f_title: "Title",
  cp_f_authority: "Authority",
  cp_auth_sole: "Sole",
  cp_auth_joint: "Joint",
  cp_auth_advisory: "Advisory",
  cp_add_sign: "Add signatory",
  cp_f_bank: "Bank details",
  cp_f_bank_help: "Correspondent bank, account, SWIFT, IBAN — this block is inserted into invoices, LC applications and payment instructions.",
  cp_f_footer: "Footer / signature block",
  cp_f_footer_help: "Small footer text appended to letters — e.g. 'Managed by AIT Technologies GmbH · HRB 12345'.",
  cp_save_btn: "Save Company Profile",
  cp_saving: "Saving…",
  cp_saved: "Company Profile · saved",
  cp_save_failed: "Save failed",
  cp_loading: "Loading…",

  // ============ Studio labels — Counterparties ============
  ctp_intro: "Directory of buyers, sellers, banks, shipowners and agents. Linked counterparties auto-fill deal parties.",
  ctp_new_btn: "New counterparty",
  ctp_search_ph: "Search legal name, country or type…",
  ctp_empty: "No counterparties yet — add your first buyer, seller or bank.",
  ctp_no_matches: "No matches for that search.",
  ctp_new_kicker: "New counterparty",
  ctp_edit_kicker: "Edit counterparty",
  ctp_untitled: "Untitled",
  ctp_f_legal_name: "Legal name *",
  ctp_f_type: "Type",
  ctp_f_country: "Country",
  ctp_f_vat: "VAT / Tax ID",
  ctp_f_address: "Registered address",
  ctp_f_risk: "Risk status",
  ctp_f_website: "Website",
  ctp_f_contacts: "Contacts",
  ctp_add_contact: "Add contact",
  ctp_contact_name: "Name",
  ctp_contact_role: "Role",
  ctp_contact_email: "Email",
  ctp_contact_phone: "Phone",
  ctp_f_bank: "Bank details",
  ctp_f_notes: "Internal notes",
  ctp_risk_std: "Standard",
  ctp_risk_watch: "Watch",
  ctp_risk_blocked: "Blocked",
  ctp_edit_btn: "Edit",
  ctp_save_btn: "Save counterparty",
  ctp_cancel_btn: "Cancel",
  ctp_confirm_delete: "Delete this counterparty?",
  ctp_load_failed: "Failed to load",
  ctp_delete_failed: "Delete failed",
  ctp_deleted: "Deleted",
  ctp_saved: "Saved",
  ctp_legal_required: "Legal name is required",

  // ============ Studio labels — Deal Data schema ============
  dd_sec_parties: "Parties",
  dd_sec_product: "Product",
  dd_sec_commercial: "Commercial Terms",
  dd_sec_delivery: "Delivery",
  dd_sec_payment: "Payment",
  dd_sec_inspection: "Inspection & Quality",
  dd_f_buyer_name: "Buyer legal name",
  dd_f_seller_name: "Seller legal name",
  dd_f_seller_country: "Seller country",
  dd_f_buyer_country: "Buyer country",
  dd_f_role: "Your role",
  dd_f_commodity: "Commodity",
  dd_f_grade: "Grade / Specification",
  dd_f_origin: "Origin",
  dd_f_quantity: "Quantity",
  dd_f_price: "Price / Formula",
  dd_f_currency: "Currency",
  dd_f_incoterms: "Incoterms",
  dd_f_total: "Total contract value",
  dd_f_load_port: "Load port",
  dd_f_discharge_port: "Discharge port",
  dd_f_shipment: "Shipment / Delivery period",
  dd_f_vessel: "Vessel / Mode of transport",
  dd_f_payment_instr: "Payment instrument",
  dd_f_issuing_bank: "Issuing bank",
  dd_f_advising_bank: "Advising bank",
  dd_f_payment_notes: "Payment terms notes",
  dd_f_inspection_agency: "Inspection agency",
  dd_f_insurance: "Insurance terms",
  dd_f_law: "Governing law",
  dd_f_arbitration: "Arbitration venue",
  dd_intro_data: "Structured fields describing the deal. These values auto-fill matching parameters in any document you draft from this workspace.",
  dd_save_data: "Save",
  dd_saving_data: "Saving…",
  dd_saved_data: "Deal data saved",

  // ============ Document Studio — Modes ============
  st_mode_create: "Create",
  st_mode_review: "Review",
  st_mode_improve: "Improve",
  st_mode_translate: "Translate",
  st_mode_compare: "Compare",
  st_mode_adapt: "Adapt",
  st_mode_reply: "Prepare Reply",
  st_help_create: "Generate a new document from template + parameters.",
  st_help_review: "Analyse an existing document — summary, missing info, red flags.",
  st_help_improve: "Tighten language, upgrade tone, remove ambiguity.",
  st_help_translate: "Translate the source document into the target language.",
  st_help_compare: "Compare two versions of a document (paste both A and B).",
  st_prefilled_from_deal: "{count} fields filled from the deal master record",
  st_help_adapt: "Adapt an existing document to new parties/terms.",
  st_help_reply: "Draft a professional reply to a received message.",
  st_risk_prefix: "Risk",
  st_risk_low: "low",
  st_risk_medium: "medium",
  st_risk_high: "high",
  st_intro: "Create and edit transaction documents manually or use AI-assisted drafting when enabled. Save documents and export to Word or PDF.",
  st_doc_title: "Document title (optional)",
  st_doc_title_ph: "e.g. LC amendment follow-up #2",
  st_source_label: "Source document / message",
  st_source_ph: "Paste the document text to review, improve, translate, adapt or reply to…",
  st_missing_required: "Missing required fields",
  st_source_required_toast: "Please paste the source document for this mode.",
  st_draft_ready: "Draft ready",
  st_draft_failed: "Draft failed",
  st_stop: "Stop",
  st_ai_unavailable: "AI drafting is not configured in this environment. Manual editing, saving and Word/PDF export remain fully available.",
  st_start_manual: "Start manual draft",
  st_manual_document: "Trade document",
  st_manual_data: "Transaction data",
  st_manual_body: "Document body",
  st_manual_placeholder: "Replace this line with the agreed document wording.",
  st_editor_placeholder: "Start a manual draft or generate with AI…",
  st_content_required: "Document content is required",
  st_save: "Save",
  st_saving: "Saving…",
  st_saved: "Document saved",
  st_save_failed: "Could not save document",
  st_unsaved: "Unsaved changes",
  st_save_before_export: "Save the latest changes before exporting.",
  st_exported: "{format} exported",
  st_export_failed: "Could not export document",
  st_archive_word: "Save Word to Files",
  st_archive_pdf: "Save PDF to Files",
  st_archiving: "Saving version…",
  st_archived: "{format} immutable version {version} saved to Files",
  st_archive_failed: "Could not save export to Files",
  st_immutable: "This archived version is immutable",

  // Release 2.2A — Dashboard (EN base)
  db_hero_kicker: "Workspace",
  db_hero_title: "Where deals become documents.",
  db_primary_cta: "Start a new transaction",
  db_secondary_doc: "Create standalone document",
  db_secondary_review: "Review existing document",
  db_secondary_ask: "Ask TradeDocs",
  db_check_title: "Get your workspace ready",
  db_check_sub: "Six one-time steps that unlock every workflow.",
  db_check_progress: "{done} of {total} complete",
  db_check_1: "Complete company profile",
  db_check_2: "Add authorised signatory",
  db_check_3: "Add bank details",
  db_check_4: "Add first counterparty",
  db_check_5: "Start first deal",
  db_check_6: "Create first document",
  db_continue_title: "Continue working",
  db_continue_empty: "Nothing in progress yet.",
  db_attention_title: "Needs attention",
  db_attention_none: "Nothing needs attention right now.",
  db_attention_intake: "Deal in intake — add core information",
  db_attention_qa: "Deal in QA — review before delivery",
  db_attention_incomplete: "Deal parameters incomplete",
  db_attention_placeholder: "Draft has bracketed placeholders",
  db_att_open: "Open",
  db_active_title: "Active deals",
  db_active_col_deal: "Deal",
  db_active_col_cp: "Counterparty",
  db_active_col_stage: "Stage",
  db_active_col_next: "Next action",
  db_active_col_attn: "Attention",
  db_active_next_intake: "Fill deal data",
  db_active_next_drafting: "Draft documents",
  db_active_next_qa: "Review & QA",
  db_active_next_delivery: "Deliver to counterparty",
  db_active_empty_title: "No active deals yet",
  db_active_empty_sub: "Start your first deal to open a transaction workspace.",
  db_active_empty_create: "Create first deal",
  db_active_empty_example: "View example deal",
  db_recent_col_doc: "Document",
  db_recent_col_ctx: "Deal / Counterparty",
  db_recent_title: "Recent documents",
  db_recent_col_ver: "Version",
  db_recent_col_updated: "Updated",
  db_recent_open: "Open",
  db_recent_continue: "Continue",
  db_recent_export: "Export",
  db_recent_placeholder_status: "Placeholders",
  db_recent_ready_status: "Ready",
  db_recent_empty_title: "No documents yet",
  db_recent_empty_sub: "Create a standalone document or open a deal to start drafting.",
  db_membership_link: "View Billing & Usage",
  db_error_load: "Couldn't load workspace data. Please refresh.",

  // Nav v2 (EN)
  d_nav_documents: "Documents",
  d_nav_documents_create: "Create",
  d_nav_documents_review: "Review",
  d_nav_documents_templates: "Templates",
  d_nav_documents_files: "Files",
  d_nav_language: "Language",
  d_nav_account: "Account",
  d_section_navigate: "Navigate",
  d_section_settings: "Settings",

  // ============ Release 2.2B — Structured Deal Workspace (EN) ============
  // Deal types
  dt_commodity_trade: "Commodity Trade",
  dt_cross_border_supply: "Cross-Border Supply",
  dt_shipping_chartering: "Shipping / Chartering",
  dt_trade_finance_prep: "Trade Finance Preparation",
  dt_corporate_dd: "Corporate / Due Diligence",
  dt_other: "Other",
  // Stages
  ds_initial_enquiry: "Initial enquiry",
  ds_counterparty_review: "Counterparty review",
  ds_negotiation: "Negotiation",
  ds_offer_quotation: "Offer / Quotation",
  ds_contract_preparation: "Contract preparation",
  ds_payment_instrument: "Payment instrument preparation",
  ds_pre_shipment: "Pre-shipment",
  ds_shipment: "Shipment",
  ds_document_presentation: "Document presentation",
  ds_completed: "Completed",
  ds_cancelled: "Cancelled",
  // Statuses
  dst_draft: "Draft",
  dst_active: "Active",
  dst_on_hold: "On hold",
  dst_needs_attention: "Needs attention",
  dst_completed: "Completed",
  dst_archived: "Archived",
  dst_closed: "Closed",
  // Party roles
  pr_buyer: "Buyer",
  pr_seller: "Seller",
  pr_broker: "Broker",
  pr_intermediary: "Intermediary",
  pr_supplier: "Supplier",
  pr_consignee: "Consignee",
  pr_notify_party: "Notify party",
  pr_carrier: "Carrier",
  pr_charterer: "Charterer",
  pr_shipowner: "Shipowner",
  pr_bank: "Bank",
  pr_inspection_company: "Inspection company",
  pr_insurance_company: "Insurance company",
  pr_other: "Other",
  // Wizard
  dw_page_title: "New deal",
  dw_page_sub: "Structured six-step transaction workspace.",
  dw_step1: "Overview",
  dw_step2: "Parties",
  dw_step3: "Goods & Commercial",
  dw_step4: "Delivery & Shipping",
  dw_step5: "Payment & Banking",
  dw_step6: "Review & Create",
  dw_back: "Back",
  dw_next: "Continue",
  dw_save_draft: "Save as draft",
  dw_create: "Create deal",
  dw_saving: "Saving…",
  dw_unsaved_confirm: "You have unsaved changes. Leave anyway?",
  dw_title_lbl: "Deal title",
  dw_reference_lbl: "Internal reference",
  dw_reference_hint: "Auto-generated. Edit if needed. Must be unique in your account.",
  dw_type_lbl: "Deal type",
  dw_owner_lbl: "Deal owner",
  dw_owner_you: "You",
  dw_description_lbl: "Description",
  dw_internal_notes_lbl: "Internal notes",
  dw_add_party: "Add party",
  dw_party_role: "Role",
  dw_party_company: "Company",
  dw_party_new_company: "+ Add new company",
  dw_party_contact: "Contact person (optional)",
  dw_party_contact_none: "— None —",
  dw_party_notes: "Notes",
  dw_party_remove: "Remove party",
  dw_party_empty: "No parties yet. Add buyer, seller, broker or other participants.",
  dw_new_co_legal: "Legal name",
  dw_new_co_type: "Counterparty type",
  dw_new_co_country: "Country",
  dw_new_co_save: "Save company",
  dw_new_co_cancel: "Cancel",
  dw_goods_commodity: "Product / Commodity",
  dw_goods_desc: "Description",
  dw_goods_spec: "Specification / grade / quality",
  dw_goods_origin: "Origin",
  dw_goods_quantity: "Quantity",
  dw_goods_unit: "Unit",
  dw_goods_tolerance: "Tolerance (± %)",
  dw_goods_unit_price: "Unit price",
  dw_goods_price_basis: "Price basis",
  dw_goods_packaging: "Packaging",
  dw_goods_inspection: "Inspection requirements",
  dw_goods_addl_terms: "Additional commercial terms",
  dw_currency: "Currency",
  dw_total_value: "Total value",
  dw_total_calc: "Calculated",
  dw_total_manual: "Manual override",
  dw_total_recalc: "Reset to calculated",
  dw_delivery_incoterms: "Incoterms rule",
  dw_delivery_incoterms_ver: "Incoterms version",
  dw_delivery_named_place: "Named place / port",
  dw_delivery_country: "Delivery country",
  dw_delivery_load: "Loading place / port",
  dw_delivery_discharge: "Discharge place / port",
  dw_delivery_window: "Shipment / delivery window",
  dw_delivery_partial: "Partial shipment allowed",
  dw_delivery_transship: "Transshipment allowed",
  dw_delivery_mode: "Transport mode",
  dw_delivery_vessel: "Vessel / voyage details",
  dw_delivery_instructions: "Shipping instructions",
  dw_pay_method: "Payment method",
  dw_pay_schedule: "Payment timing / schedule",
  dw_pay_advance_pct: "Advance %",
  dw_pay_balance: "Balance conditions",
  dw_pay_instrument: "Banking instrument",
  dw_pay_issuing_bank: "Issuing bank",
  dw_pay_advising_bank: "Advising / receiving bank",
  dw_pay_amount: "Instrument amount",
  dw_pay_currency: "Instrument currency",
  dw_pay_validity: "Validity / expiry",
  dw_pay_presentation: "Presentation period",
  dw_pay_confirmed: "Confirmation required",
  dw_pay_transferable: "Transferable",
  dw_pay_notes: "Payment notes",
  dw_review_title: "Review your deal",
  dw_review_sub: "Verify the entered information. You can still return to any step to correct it. Missing information will remain flagged on the deal workspace.",
  dw_review_no_data: "Not provided",
  // Yes/no
  dw_yes: "Yes",
  dw_no: "No",
  // Dashboard/list
  dl_col_reference: "Reference",
  dl_col_buyer: "Buyer",
  dl_col_seller: "Seller",
  dl_col_product: "Product",
  dl_col_total: "Total",
  dl_col_status: "Status",
  dl_col_next_action: "Next action",
  dl_filter_status: "Status",
  dl_filter_stage: "Stage",
  dl_filter_all: "All",
  dl_sort_reference: "Reference",
  dl_deals_new_btn: "New deal",
  dl_include_archived: "Include archived",
  // Missing info
  mi_title: "Missing information",
  mi_sub: "Deterministic checklist based on stored data. Complete items to reduce risk before contract signing.",
  mi_ok: "All critical information is present.",
  mi_no_title: "Deal title",
  mi_no_type: "Deal type",
  mi_no_buyer: "Buyer",
  mi_no_seller: "Seller",
  mi_no_product: "Product / commodity",
  mi_no_quantity: "Quantity",
  mi_no_currency: "Currency",
  mi_no_incoterms: "Incoterms",
  mi_no_named_place: "Named place / port",
  mi_no_delivery_window: "Shipment window",
  mi_no_payment_method: "Payment method",
  mi_no_issuing_bank: "Issuing bank (LC)",
  mi_edit: "Fix",
  // Next action
  na_title: "Next action",
  na_none: "No next action set.",
  na_action_lbl: "Action",
  na_assignee_lbl: "Responsible",
  na_due_lbl: "Due date",
  na_note_lbl: "Note",
  na_save: "Save",
  na_edit: "Edit next action",
  na_clear: "Clear next action",
  // Workspace tabs
  ws_tab_overview: "Overview",
  ws_tab_parties: "Parties",
  ws_tab_commercial: "Commercial terms",
  ws_tab_delivery: "Delivery & shipping",
  ws_tab_payment: "Payment & banking",
  ws_tab_documents: "Documents",
  ws_tab_consistency: "Consistency",
  ws_tab_files: "Files",
  ws_tab_sharing: "External access",
  ws_tab_activity: "Activity",
  ws_tab_conversation: "Conversation",
  conversation_title: "Deal conversation",
  conversation_sub: "Keep comments, internal notes, correspondence and system activity in one traceable stream.",
  conversation_kind_comment: "Comment",
  conversation_kind_note: "Internal note",
  conversation_kind_correspondence: "Correspondence",
  conversation_outbound: "Outbound",
  conversation_inbound: "Inbound",
  conversation_subject: "Subject",
  conversation_from: "From",
  conversation_to: "To",
  conversation_placeholder: "Write a comment, note or correspondence record…",
  conversation_mentions_empty: "Add deal parties to enable mention shortcuts.",
  conversation_send: "Add to conversation",
  conversation_stream: "Conversation & activity",
  conversation_reply: "Reply",
  conversation_replying_to: "Replying to",
  conversation_delete: "Delete",
  conversation_delete_confirm: "Delete this conversation entry?",
  conversation_delete_blocked: "Delete its replies first.",
  conversation_saved: "Added to conversation",
  conversation_deleted: "Conversation entry deleted",
  ws_stage_lbl: "Stage",
  ws_status_lbl: "Status",
  ws_owner_lbl: "Owner",
  ws_responsible_lbl: "Responsible",
  ws_responsible_none: "Not assigned",
  ws_updated_lbl: "Last update",
  ws_edit: "Edit",
  ws_save: "Save changes",
  ws_saving: "Saving…",
  ws_saved: "Saved",
  ws_cancel_edit: "Cancel",
  ws_archive: "Archive deal",
  ws_restore: "Restore deal",
  ws_delete: "Delete permanently",
  ws_confirm_archive: "Archive this deal? It will be hidden from active lists but preserved.",
  ws_confirm_restore: "Restore this archived deal to active?",
  ws_confirm_delete: "Delete permanently? This cannot be undone.",
  ws_delete_blocked: "This deal has {n} linked document(s). Archive it instead, or delete the documents first.",
  ws_docs_empty: "No documents linked to this deal yet.",
  ws_docs_new: "Create document for this deal",
  doc_pack_kicker: "Recommended pack",
  doc_pack_title: "Documents suggested for this deal",
  doc_pack_intro: "Based on the deal type, current stage, delivery terms and payment instrument.",
  doc_pack_ready: "created",
  doc_pack_create: "Create",
  doc_pack_open: "Open",
  doc_pack_reason_type: "Recommended for this deal type",
  doc_pack_reason_stage: "Recommended for the current stage",
  doc_pack_reason_payment: "Required by the selected payment instrument",
  doc_pack_reason_shipping: "Recommended for the selected delivery terms",
  doc_pack_disclaimer: "Operational recommendation only. Confirm contractual, banking and regulatory requirements for the actual transaction.",
  master_change_confirm: "This deal already has {count} document(s). Saving master-data changes may make them outdated. Continue?",
  master_stale_title: "{count} document(s) require review",
  master_stale_intro: "The deal master data changed after these documents were created. Open each document, verify the affected terms and save it before continuing approval.",
  master_stale_badge: "Master data changed",
  master_stale_workflow_blocked: "Review and save this document after the deal-data change before continuing approval.",
  consistency_kicker: "R3.3 transaction control",
  consistency_title: "Cross-document consistency",
  consistency_intro: "Compare structured values across the deal's extracted documents and the current Deal Master, then resolve or explicitly accept every material discrepancy.",
  consistency_scope: "Only medium- and high-confidence extracted values are compared. Missing fields are not treated as conflicts because different document types may legitimately contain different data.",
  consistency_run: "Run comparison",
  consistency_running: "Comparing…",
  consistency_empty: "No consistency report yet",
  consistency_empty_hint: "Extract structured data from at least two files in Document Review, then run the comparison here.",
  consistency_documents: "Documents compared",
  consistency_total: "Discrepancies",
  consistency_open: "Open",
  consistency_high: "High impact",
  consistency_clear: "No material discrepancies were found.",
  consistency_found: "{count} discrepancy(s) require attention",
  consistency_outdated: "The Deal Master changed after this report was created. Run the comparison again before relying on the result.",
  consistency_deal_master: "Deal Master",
  consistency_severity_high: "High",
  consistency_severity_medium: "Medium",
  consistency_status_open: "Open",
  consistency_status_resolved: "Resolved",
  consistency_status_accepted: "Accepted exception",
  consistency_resolve: "Mark resolved",
  consistency_accept: "Accept exception",
  consistency_reopen: "Reopen",
  consistency_resolve_prompt: "Describe how this discrepancy was corrected:",
  consistency_accept_prompt: "Explain why this difference is acceptable:",
  consistency_note_required: "A decision note is required",
  consistency_resolution: "Decision",
  consistency_report_meta: "Report {date} · Deal Master revision {revision}",
  consistency_load_failed: "Could not load the consistency report",
  consistency_run_failed: "Could not compare the documents",
  consistency_update_failed: "Could not update the discrepancy",
  consistency_status_updated: "Discrepancy status updated",
  ws_docs_edit: "Edit",
  doc_status_draft: "Draft",
  doc_status_in_review: "In review",
  doc_status_changes_requested: "Changes requested",
  doc_status_approved: "Approved",
  doc_status_final: "Final",
  doc_action_submit: "Request review",
  doc_action_approve: "Approve",
  doc_action_changes: "Request changes",
  doc_action_final: "Mark final",
  doc_action_reopen: "Reopen",
  doc_workflow_changes_prompt: "Describe the required changes:",
  doc_workflow_note_prompt: "Optional decision note:",
  doc_workflow_updated: "Document status updated",
  doc_workflow_conflict: "The document status changed. Reload and try again.",
  ws_activity_empty: "No activity recorded yet.",
  ws_ev_created: "Deal created",
  ws_ev_stage: "Stage changed to {v}",
  ws_ev_status: "Status changed to {v}",
  ws_ev_updated: "Details updated",
  ws_ev_next_action: "Next action updated",
  ws_ev_archived: "Deal archived",
  ws_ev_restored: "Deal restored",
  ws_ev_draft_linked: "Document {code} v{v} added",
  ws_ev_document_workflow: "Document {code}: {status} · {actor}",
  // Errors / toasts
  err_reference_taken: "That reference is already used by another of your deals.",
  err_deal_has_documents: "Cannot delete: deal has linked documents.",
  err_generic_save: "Save failed. Please try again.",
  ok_deal_created: "Deal created",
  ok_deal_updated: "Deal updated",
  ok_deal_archived: "Deal archived",
  ok_deal_restored: "Deal restored",
  ok_deal_deleted: "Deal deleted",
  ok_draft_saved: "Draft saved",
};

// Italian — public sections only. Portal keeps EN.
const IT_OVERRIDES = {
  nav_product: "Prodotto",
  nav_documents: "Documenti",
  nav_how: "Come funziona",
  nav_use_cases: "Casi d'uso",
  nav_pricing: "Prezzi",
  nav_about: "Chi siamo",
  nav_contact: "Contatti",
  nav_login: "Accedi",
  nav_join: "Crea 3 documenti gratis",
  nav_workspace: "Area personale",
  nav_logout: "Esci",
  nav_demo: "Guarda la demo",

  hero_label: "TRADEDOCS AI DESK",
  hero_title: "Documenti professionali per operazioni internazionali — preparati in un unico spazio di lavoro intelligente.",
  hero_deal_line: "Acquirente e venditore. Un unico spazio privato per la trattativa.",
  hero_sub: "Create documenti commerciali con l’AI, condivideteli con la controparte e discutete le modifiche in uno spazio di lavoro privato.",
  hero_cta_primary: "Crea 3 documenti gratis",
  hero_cta_secondary: "Esplora la piattaforma",
  hero_cta_login: "Accedi",
  trust_1: "Flussi di commercio internazionale",
  trust_2: "Inglese, italiano, tedesco, spagnolo, cinese e russo",
  trust_3: "Output modificabile",
  trust_4: "Trattamento riservato",
  trust_5: "Revisione esperta opzionale",
  hero_mockup_caption: "Dai dati strutturati della transazione a un documento professionale modificabile.",

  product_label: "IL PRODOTTO",
  product_title: "Più di una libreria di modelli.",
  product_sub: "TradeDocs combina modelli strutturati, dati della transazione e drafting assistito da AI in un unico spazio di lavoro professionale.",
  product_card_1_title: "Modelli strutturati",
  product_card_1_desc: "Modelli pensati per reali flussi di commercio internazionale e shipping.",
  product_card_2_title: "Drafting basato sull'operazione",
  product_card_2_desc: "Documenti generati usando i parametri specifici della transazione del cliente.",
  product_card_3_title: "Revisione documentale",
  product_card_3_desc: "Termini chiave, informazioni mancanti e possibili incongruenze evidenziati prima dell'uso.",
  product_card_4_title: "Dati aziendali riutilizzabili",
  product_card_4_desc: "Dettagli aziendali, controparti e informazioni transazionali salvati per i documenti futuri.",

  three_ways_title: "Inizia nel modo più adatto a te.",
  three_ways_1_title: "Usa un modello",
  three_ways_1_desc: "Seleziona un modello professionale, inserisci i parametri della transazione e genera una bozza strutturata.",
  three_ways_1_cta: "Sfoglia i modelli",
  three_ways_2_title: "Carica un documento",
  three_ways_2_desc: "Carica un documento esistente per riassumerlo, identificare informazioni mancanti o preparare una versione riveduta.",
  three_ways_2_cta: "Revisiona un documento",
  three_ways_3_title: "Descrivi ciò che ti serve",
  three_ways_3_desc: "Spiega il tuo obiettivo commerciale e lascia che TradeDocs consigli il documento o flusso più adatto.",
  three_ways_3_cta: "Descrivi la richiesta",
  three_ways_3_badge: "Il più semplice",

  cats_title: "Documenti per operazioni internazionali reali.",
  cats_view: "Vedi i modelli",
  cat_commercial: "Documenti commerciali",
  cat_commercial_desc: "Atti di apertura, offerte aziendali e corrispondenza business.",
  cat_trade_finance: "Trade Finance",
  cat_trade_finance_desc: "Lettere bancarie, crediti documentari e verifiche di completezza.",
  cat_shipping: "Shipping & Chartering",
  cat_shipping_desc: "Scambi di nolo, laytime, reclami e dati nave.",
  cat_corporate: "Corporate & Due Diligence",
  cat_corporate_desc: "KYC, questionari DD e dichiarazioni aziendali.",
  cat_contracts: "Contratti e corrispondenza formale",
  cat_contracts_desc: "Contratti di fornitura, notifiche, emendamenti e lettere di accompagnamento.",
  cat_management: "Sintesi direzionali",
  cat_management_desc: "Sintesi esecutive e raccomandazioni pratiche sui prossimi passi.",

  preview_label: "DENTRO TRADEDOCS",
  preview_title: "Un unico spazio di lavoro per documenti e operazioni.",
  preview_step_1: "Seleziona un modello",
  preview_step_2: "Aggiungi i parametri della transazione",
  preview_step_3: "Carica documenti di supporto",
  preview_step_4: "Genera una bozza professionale",
  preview_step_5: "Rivedi le informazioni mancanti",
  preview_step_6: "Esporta il documento",

  how_title: "Dai dati della transazione al documento finito.",
  how_1_t: "Scegli",
  how_1_d: "Seleziona un modello, carica un file o descrivi l'esito richiesto.",
  how_2_t: "Aggiungi informazioni",
  how_2_d: "Inserisci parti, prodotto, quantità, prezzo, Incoterms e altri dettagli rilevanti.",
  how_3_t: "Genera",
  how_3_d: "TradeDocs struttura le informazioni e prepara una bozza professionale.",
  how_4_t: "Rivedi",
  how_4_d: "Controlla dettagli mancanti, suggerimenti e possibili incongruenze.",
  how_5_t: "Esporta",
  how_5_d: "Salva il documento ed esportalo in un formato modificabile.",

  uc_title: "Costruito per il lavoro commerciale reale.",
  uc_1_t: "Commodity Trading",
  uc_1_d: "Prepara LOI, offerta commerciale, questionario DD e corrispondenza di follow-up.",
  uc_2_t: "Shipping & Chartering",
  uc_2_d: "Prepara domande di chartering, recap, reclami e corrispondenza owner-charterer.",
  uc_3_t: "Trade Finance",
  uc_3_d: "Struttura bozze BCL, SBLC e LC con checklist documentali.",
  uc_4_t: "Fornitura cross-border",
  uc_4_d: "Prepara proforma, contratti di fornitura, offerte commerciali e corrispondenza formale.",
  uc_5_t: "DD delle controparti",
  uc_5_d: "Genera richieste KYC e DD strutturate in base al tipo di transazione.",
  uc_6_t: "Reporting direzionale",
  uc_6_d: "Trasforma un dossier documentale complesso in un management summary conciso.",

  ai_title: "Drafting assistito da AI con logica orientata alla transazione.",
  ai_sub: "TradeDocs aiuta a strutturare le informazioni, preparare bozze e individuare dettagli mancanti. La piattaforma è pensata per commercio internazionale e shipping, non per la generazione di testo generico.",
  ai_b_1: "Prime bozze più rapide",
  ai_b_2: "Struttura documentale coerente",
  ai_b_3: "Informazioni aziendali riutilizzabili",
  ai_b_4: "Output multilingua",
  ai_b_5: "Documenti modificabili",
  ai_b_6: "Controlli sulle informazioni mancanti",
  ai_b_7: "Supporto esperto opzionale",

  ps_title: "Serve supporto professionale per una transazione complessa?",
  ps_sub: "Revisione umana e assistenza dedicata sono disponibili su richiesta per incarichi complessi di shipping, trade finance o multi-parte.",
  ps_cta: "Richiedi supporto esperto",

  conf_title: "Progettato per documenti aziendali riservati.",
  conf_1: "Accesso controllato",
  conf_2: "Account utenti protetti",
  conf_3: "Trattamento file riservato",
  conf_4: "Nessun uso pubblico dei documenti clienti",
  conf_5: "NDA disponibile su richiesta",
  conf_6: "Opzioni di eliminazione documenti",
  conf_7: "Separazione dati per organizzazione",
  conf_8: "Informazioni chiare sulla conservazione",

  mem_label: "MEMBERSHIP",
  mem_title: "Un abbonamento per la documentazione internazionale quotidiana.",
  mem_sub: "Accedi alla libreria modelli, agli strumenti di drafting AI, ai flussi documentali e ai dati transazionali salvati con una sola membership mensile.",
  mem_note: "Crea ed esporta tre documenti completi senza carta, poi scegli un piano mensile.",
  mem_expert: "Revisione umana esperta e supporto transazionale su misura possono essere ordinati separatamente.",

  ea_title: "Prepara oggi il tuo primo documento commerciale.",
  ea_sub: "Crea un workspace, scegli un modello professionale ed esporta fino a tre documenti completi senza carta.",
  ea_first: "Nome",
  ea_last: "Cognome",
  ea_company: "Azienda",
  ea_email: "Email aziendale",
  ea_country: "Paese",
  ea_area: "Area di lavoro principale",
  ea_lang: "Lingua preferita",
  ea_doc: "Documento principale richiesto",
  ea_message: "Messaggio (facoltativo)",
  ea_cta: "Crea account gratuito",
  ea_ok: "Grazie. La tua richiesta è stata ricevuta.",
  ea_err: "Qualcosa è andato storto. Riprova.",
  ea_privacy: "Inviando confermi che TradeDocs può contattarti in merito alla richiesta. Trattamento riservato.",

  ar_title: "Hai un documento o una transazione da preparare?",
  ar_sub: "Invia un breve brief: la nostra desk risponderà.",
  ar_cta: "Invia richiesta",
  ar_ok: "Grazie. Ti contatteremo entro un giorno lavorativo.",

  faq_title: "Domande frequenti.",

  ft_product: "Prodotto",
  ft_docs: "Aree documentali",
  ft_company: "Azienda",
  ft_legal: "Legale",
  ft_languages: "Lingue",
  ft_rights: "Tutti i diritti riservati.",
  ft_tagline: "Spazio di lavoro documentale assistito da AI per commercio internazionale, shipping e trade finance.",

  // Client Portal — IT
  d_nav_dashboard: "Cruscotto",
  d_nav_demo: "Guarda la demo",
  d_nav_deals: "Trattative",
  d_nav_studio: "Studio Documenti",
  d_nav_drafting: "Studio Documenti",
  d_nav_review: "Revisione Documenti",
  d_nav_templates: "Libreria Modelli",
  d_nav_files: "File",
  d_nav_counterparties: "Controparti",
  d_nav_company: "Profilo Aziendale",
  d_nav_expert: "Supporto Esperto",
  d_nav_requests: "Le mie Richieste",
  d_nav_intakes: "Le mie Richieste",
  d_nav_billing: "Fatturazione e Utilizzo",
  d_nav_settings: "Impostazioni",
  d_nav_organization: "Organizzazione e team",
  d_nav_logout: "Esci",
  d_section_workspace: "Spazio di lavoro",
  d_section_business: "Dati aziendali",
  d_section_account: "Account",
  db_welcome: "Bentornato",
  db_welcome_sub: "Prosegui una transazione attiva o inizia un nuovo documento.",
  db_qa_create: "Crea un documento",
  db_qa_create_sub: "Parti da un modello professionale.",
  db_qa_review: "Revisiona un documento",
  db_qa_review_sub: "Carica e analizza un documento esistente.",
  db_qa_new_deal: "Nuova trattativa",
  db_qa_new_deal_sub: "Apri un nuovo workspace transazionale.",
  db_qa_describe: "Descrivi ciò che ti serve",
  db_qa_describe_sub: "Lascia che TradeDocs consigli un flusso.",
  db_section_active: "Trattative attive",
  db_section_attention: "Documenti che richiedono attenzione",
  db_section_recent: "Documenti recenti",
  db_section_membership: "Abbonamento e utilizzo",
  db_view_all: "Vedi tutti",
  db_empty_deals: "Nessuna trattativa attiva — inizia la prima.",
  db_empty_attention: "Al momento nulla richiede attenzione.",
  db_empty_docs: "Ancora nessun documento.",
  db_open: "Apri",
  db_plan_early: "Accesso Anticipato",
  db_renews: "Rinnovo",
  db_usage_docs: "Documenti generati",
  db_usage_pages: "Pagine elaborate",
  db_usage_deals: "Trattative attive",
  db_usage_storage: "Archiviazione",
  db_coming_soon: "In arrivo",
  db_coming_soon_sub: "Questa area è in preparazione per la prossima release del Client Portal.",
  db_notify: "Avvisami quando è disponibile",
  d_deals_title: "Trattative attive",
  d_deals_new: "Nuova trattativa",
  d_deals_empty: "Ancora nessuna trattativa. Crea la prima.",
  d_deals_kicker: "Spazio di lavoro",
  d_deals_loading: "Caricamento…",
  d_col_title: "Titolo",
  d_col_counterparty: "Controparte",
  d_col_type: "Linea",
  d_col_stage: "Fase",
  d_col_status: "Stato",
  d_col_updated: "Aggiornato",
  d_stage_intake: "Ricezione",
  d_stage_drafting: "Redazione",
  d_stage_qa: "Controllo qualità",
  d_stage_delivery: "Consegna",
  d_type_commercial: "Redazione commerciale",
  d_type_documentary: "Supporto documentale",
  d_type_shipping: "Shipping e Chartering",
  d_type_management: "Management Desk",
  d_new_kicker: "Nuova trattativa",
  d_new_title: "Apri una nuova trattativa",
  d_new_field_title: "Titolo",
  d_new_field_counterparty: "Controparte",
  d_new_field_type: "Linea",
  d_new_field_value: "Valore",
  d_new_field_notes: "Note",
  d_new_value_ph: "es. USD 2,5 mln",
  d_new_submit: "Crea",
  d_new_close: "Chiudi",
  d_toast_created: "Trattativa creata",
  d_toast_create_failed: "Creazione trattativa non riuscita",
  d_toast_stage_updated: "Fase aggiornata",
  d_toast_failed: "Operazione non riuscita",
  d_toast_deleted: "Eliminata",
  d_deals_delete_action: "Elimina",
  dl_search_ph: "Cerca trattative…",
  dl_sort_updated: "Aggiornate di recente",
  dl_sort_created: "Più recenti",
  dl_sort_title: "Titolo A→Z",
  dl_sort_stage: "Fase",
  dl_no_match: "Nessuna trattativa corrisponde alla ricerca.",

  // Landing form options — IT
  ea_area_opt_1: "Commercio internazionale",
  ea_area_opt_2: "Commodity Trading",
  ea_area_opt_3: "Shipping",
  ea_area_opt_4: "Chartering",
  ea_area_opt_5: "Trade Finance",
  ea_area_opt_6: "Import ed Export",
  ea_area_opt_7: "Servizi aziendali",
  ea_area_opt_8: "Altro",
  ar_type_opt_1: "Documento commerciale",
  ar_type_opt_2: "Trade finance",
  ar_type_opt_3: "Shipping / Chartering",
  ar_type_opt_4: "Corporate / DD",
  ar_type_opt_5: "Altro",
  ar_urgency_1: "Normale",
  ar_urgency_2: "Alta",
  ar_urgency_3: "Urgente",

  // Footer links — IT
  ft_link_overview: "Panoramica del prodotto",
  ft_link_pricing: "Prezzi",
  ft_link_templates: "Modelli",
  ft_link_how: "Come funziona",
  ft_link_use_cases: "Casi d'uso",
  ft_link_early: "Crea account gratuito",
  ft_link_login: "Accedi",
  ft_link_about: "Chi siamo",
  ft_link_contact: "Contatti",
  ft_link_support: "Supporto esperto",
  ft_link_privacy: "Informativa sulla privacy",
  ft_link_terms: "Condizioni di servizio",
  ft_link_subscription: "Abbonamento e cancellazione",
  ft_link_cookies: "Cookie Policy",
  ft_link_dp: "Trattamento dei dati",
  ft_link_nda: "Informazioni NDA",

  // FAQ — IT
  faq_q_1: "Che cos'è TradeDocs?",
  faq_a_1: "TradeDocs è uno spazio di lavoro documentale per professionisti del commercio internazionale. Combina modelli strutturati, dati transazionali e redazione assistita da AI per preparare documenti commerciali, di shipping e di trade finance in modo più rapido e coerente.",
  faq_q_2: "TradeDocs è uno studio legale?",
  faq_a_2: "No. TradeDocs è una piattaforma documentale professionale. Non è uno studio legale e non fornisce consulenza legale. Tutto l'output è una bozza professionale sulla quale l'utente mantiene il pieno controllo.",
  faq_q_3: "Devo sapere quale modello usare?",
  faq_a_3: "Non necessariamente. Puoi partire da un modello che già conosci, caricare un documento esistente o semplicemente descrivere ciò che ti serve — TradeDocs suggerirà il documento o il flusso più adatto.",
  faq_q_4: "Quali tipi di documento sono disponibili?",
  faq_a_4: "Documenti commerciali (LOI, ICPO, offerte), trade finance (BCL, LC, SBLC), shipping e chartering (recap, laytime, claim), corporate & DD (KYC, DDQ) e contratti / corrispondenza formale.",
  faq_q_5: "Posso caricare un documento esistente?",
  faq_a_5: "Sì. Puoi caricare un documento per ottenerne una sintesi, individuare informazioni mancanti o preparare una versione rivista.",
  faq_q_6: "Quali lingue sono supportate?",
  faq_a_6: "Il sito pubblico e l'interfaccia del prodotto sono disponibili in inglese, italiano e tedesco. I documenti possono essere generati e modificati in più lingue in base al modello.",
  faq_q_7: "Posso esportare i documenti in Word o PDF?",
  faq_a_7: "Sì. Tutte le bozze possono essere esportate in Word (.docx) modificabile e in PDF per un uso immediato o per l'ulteriore editing al di fuori di TradeDocs.",
  faq_q_8: "È inclusa una revisione umana da parte di esperti?",
  faq_a_8: "La revisione automatica è inclusa. La revisione umana da parte di esperti e il supporto transazionale su misura possono essere ordinati separatamente per incarichi complessi.",
  faq_q_9: "Come vengono trattate le mie informazioni?",
  faq_a_9: "TradeDocs adotta controllo d'accesso, account utente protetti e separazione dei dati per organizzazione. I documenti dei clienti non sono utilizzati per alcuna finalità pubblica. NDA disponibile su richiesta.",
  faq_q_10: "Posso provare TradeDocs prima dell'abbonamento?",
  faq_a_10: "Sì. Una nuova organizzazione può creare, modificare ed esportare tre documenti completi senza carta. Per crearne altri è necessario un abbonamento mensile.",
  faq_q_11: "La mia azienda può utilizzare TradeDocs come team?",
  faq_a_11: "Sì. TradeDocs supporta account aziendali, modelli condivisi e workspace transazionali condivisi. Le funzionalità team sono in espansione in vista del lancio pubblico.",

  // Auth — IT
  auth_signin: "Accedi",
  auth_signup: "Crea account",
  auth_email: "Email",
  auth_password: "Password",
  auth_name: "Nome completo",
  auth_company: "Azienda (facoltativo)",
  auth_no_account: "Non hai un account?",
  auth_have_account: "Sei già registrato?",
  auth_login_btn: "Accedi",
  auth_register_btn: "Registrati",
  auth_err_login: "Email o password non valida",
  auth_hero_title: "Il desk esterno che le tue trattative meritano.",
  auth_hero_sub: "Accedi al tuo spazio di lavoro — trattative, bozze e libreria dei modelli.",
  auth_welcome_back: "Bentornato.",
  auth_open_desk: "Apri il tuo desk.",
  auth_trust_line: "Riservato · NDA · Cifrato",
  auth_loading: "…",
  auth_forgot_password: "Password dimenticata?",
  auth_privacy_signup: "Creando un account, confermi di aver preso visione del trattamento dei dati personali:",
  auth_privacy_signin: "Informazioni sul trattamento dei dati personali:",

  // Studio — IT
  cp_intro: "Salvato una volta, riutilizzato ovunque. Intestazioni legali, firmatari e dati bancari precompilano automaticamente i documenti generati.",
  cp_sec_legal: "Identità legale",
  cp_sec_contact: "Contatti",
  cp_sec_defaults: "Impostazioni predefinite",
  cp_sec_signatories: "Firmatari",
  cp_sec_bank: "Banca e piè di pagina",
  cp_f_legal_name: "Ragione sociale",
  cp_f_country: "Paese",
  cp_f_registration: "Numero di registrazione",
  cp_f_vat: "Partita IVA / Codice fiscale",
  cp_f_registered_addr: "Sede legale",
  cp_f_operational_addr: "Sede operativa",
  cp_f_website: "Sito web",
  cp_f_phone: "Telefono",
  cp_f_email: "Email pubblica",
  cp_f_currency: "Valuta predefinita",
  cp_f_lang: "Lingua predefinita dei documenti",
  cp_f_name: "Nome",
  cp_f_title: "Ruolo",
  cp_f_authority: "Poteri",
  cp_auth_sole: "Individuale",
  cp_auth_joint: "Congiunta",
  cp_auth_advisory: "Consultiva",
  cp_add_sign: "Aggiungi firmatario",
  cp_f_bank: "Dati bancari",
  cp_f_bank_help: "Banca corrispondente, conto, SWIFT, IBAN — questo blocco viene inserito in fatture, richieste LC e istruzioni di pagamento.",
  cp_f_footer: "Piè di pagina / blocco firma",
  cp_f_footer_help: "Testo breve aggiunto in fondo alle lettere — es. 'Gestito da AIT Technologies GmbH · HRB 12345'.",
  cp_save_btn: "Salva profilo azienda",
  cp_saving: "Salvataggio…",
  cp_saved: "Profilo aziendale · salvato",
  cp_save_failed: "Salvataggio fallito",
  cp_loading: "Caricamento…",

  ctp_intro: "Rubrica di acquirenti, venditori, banche, armatori e agenti. Le controparti collegate precompilano le parti della trattativa.",
  ctp_new_btn: "Nuova controparte",
  ctp_search_ph: "Cerca per ragione sociale, paese o tipo…",
  ctp_empty: "Nessuna controparte ancora — aggiungi il primo acquirente, venditore o banca.",
  ctp_no_matches: "Nessun risultato per questa ricerca.",
  ctp_new_kicker: "Nuova controparte",
  ctp_edit_kicker: "Modifica controparte",
  ctp_untitled: "Senza titolo",
  ctp_f_legal_name: "Ragione sociale *",
  ctp_f_type: "Tipo",
  ctp_f_country: "Paese",
  ctp_f_vat: "Partita IVA / Codice fiscale",
  ctp_f_address: "Sede legale",
  ctp_f_risk: "Stato di rischio",
  ctp_f_website: "Sito web",
  ctp_f_contacts: "Contatti",
  ctp_add_contact: "Aggiungi contatto",
  ctp_contact_name: "Nome",
  ctp_contact_role: "Ruolo",
  ctp_contact_email: "Email",
  ctp_contact_phone: "Telefono",
  ctp_f_bank: "Dati bancari",
  ctp_f_notes: "Note interne",
  ctp_risk_std: "Standard",
  ctp_risk_watch: "In osservazione",
  ctp_risk_blocked: "Bloccato",
  ctp_edit_btn: "Modifica",
  ctp_save_btn: "Salva controparte",
  ctp_cancel_btn: "Annulla",
  ctp_confirm_delete: "Eliminare questa controparte?",
  ctp_load_failed: "Caricamento fallito",
  ctp_delete_failed: "Eliminazione fallita",
  ctp_deleted: "Eliminato",
  ctp_saved: "Salvato",
  ctp_legal_required: "La ragione sociale è obbligatoria",

  dd_sec_parties: "Parti",
  dd_sec_product: "Prodotto",
  dd_sec_commercial: "Termini commerciali",
  dd_sec_delivery: "Consegna",
  dd_sec_payment: "Pagamento",
  dd_sec_inspection: "Ispezione e qualità",
  dd_f_buyer_name: "Ragione sociale acquirente",
  dd_f_seller_name: "Ragione sociale venditore",
  dd_f_seller_country: "Paese del venditore",
  dd_f_buyer_country: "Paese dell'acquirente",
  dd_f_role: "Il tuo ruolo",
  dd_f_commodity: "Merce",
  dd_f_grade: "Grado / Specifiche",
  dd_f_origin: "Origine",
  dd_f_quantity: "Quantità",
  dd_f_price: "Prezzo / Formula",
  dd_f_currency: "Valuta",
  dd_f_incoterms: "Incoterms",
  dd_f_total: "Valore totale del contratto",
  dd_f_load_port: "Porto di carico",
  dd_f_discharge_port: "Porto di scarico",
  dd_f_shipment: "Periodo di spedizione / consegna",
  dd_f_vessel: "Nave / Modalità di trasporto",
  dd_f_payment_instr: "Strumento di pagamento",
  dd_f_issuing_bank: "Banca emittente",
  dd_f_advising_bank: "Banca avvisante",
  dd_f_payment_notes: "Note sui termini di pagamento",
  dd_f_inspection_agency: "Ente di ispezione",
  dd_f_insurance: "Termini di assicurazione",
  dd_f_law: "Legge applicabile",
  dd_f_arbitration: "Sede arbitrale",
  dd_intro_data: "Campi strutturati che descrivono la trattativa. Questi valori precompilano i parametri corrispondenti nei documenti redatti da questo workspace.",
  dd_save_data: "Salva",
  dd_saving_data: "Salvataggio…",
  dd_saved_data: "Dati trattativa salvati",

  st_mode_create: "Crea",
  st_mode_review: "Revisiona",
  st_mode_improve: "Migliora",
  st_mode_translate: "Traduci",
  st_mode_compare: "Confronta",
  st_mode_adapt: "Adatta",
  st_mode_reply: "Prepara risposta",
  st_help_create: "Genera un nuovo documento da modello + parametri.",
  st_help_review: "Analizza un documento esistente — sintesi, dati mancanti, punti critici.",
  st_help_improve: "Chiarisce il linguaggio, migliora il tono, rimuove ambiguità.",
  st_help_translate: "Traduce il documento sorgente nella lingua di destinazione.",
  st_help_compare: "Confronta due versioni di un documento (incolla sia A che B).",
  st_prefilled_from_deal: "{count} campi compilati dai dati principali della trattativa",
  st_help_adapt: "Adatta un documento esistente a nuove parti/condizioni.",
  st_help_reply: "Redigi una risposta professionale a un messaggio ricevuto.",
  st_risk_prefix: "Rischio",
  st_risk_low: "basso",
  st_risk_medium: "medio",
  st_risk_high: "alto",
  st_intro: "Redigi, revisiona, traduci, adatta e migliora documenti commerciali con l'AI. Scegli un modello, una modalità e una lingua, poi genera.",
  st_doc_title: "Titolo del documento (opzionale)",
  st_doc_title_ph: "es. Follow-up modifica LC #2",
  st_source_label: "Documento / messaggio sorgente",
  st_source_ph: "Incolla qui il testo del documento da revisionare, migliorare, tradurre, adattare o rispondere…",
  st_missing_required: "Campi obbligatori mancanti",
  st_source_required_toast: "Incolla il documento sorgente per questa modalità.",
  st_draft_ready: "Bozza pronta",
  st_draft_failed: "Generazione fallita",
  st_stop: "Stop",
  st_archive_word: "Salva Word nei file",
  st_archive_pdf: "Salva PDF nei file",
  st_archiving: "Salvataggio versione…",
  st_archived: "Versione immutabile {version} in {format} salvata nei file",
  st_archive_failed: "Impossibile salvare l'esportazione nei file",
  st_immutable: "Questa versione archiviata non è modificabile",

  // Release 2.2A — IT
  db_hero_kicker: "Spazio di lavoro",
  db_hero_title: "Dove le trattative diventano documenti.",
  db_primary_cta: "Avvia una nuova transazione",
  db_secondary_doc: "Crea documento autonomo",
  db_secondary_review: "Revisiona documento esistente",
  db_secondary_ask: "Chiedi a TradeDocs",
  db_check_title: "Prepara il tuo spazio di lavoro",
  db_check_sub: "Sei passi una tantum che sbloccano ogni flusso di lavoro.",
  db_check_progress: "{done} di {total} completati",
  db_check_1: "Completa il profilo aziendale",
  db_check_2: "Aggiungi un firmatario autorizzato",
  db_check_3: "Aggiungi i dati bancari",
  db_check_4: "Aggiungi la prima controparte",
  db_check_5: "Avvia la prima trattativa",
  db_check_6: "Crea il primo documento",
  db_continue_title: "Continua a lavorare",
  db_continue_empty: "Nulla in corso al momento.",
  db_attention_title: "Richiede attenzione",
  db_attention_none: "Nulla richiede attenzione al momento.",
  db_attention_intake: "Trattativa in ricezione — aggiungi informazioni chiave",
  db_attention_qa: "Trattativa in QA — revisiona prima della consegna",
  db_attention_incomplete: "Parametri della trattativa incompleti",
  db_attention_placeholder: "La bozza contiene placeholder tra parentesi",
  db_att_open: "Apri",
  db_active_title: "Trattative attive",
  db_active_col_deal: "Trattativa",
  db_active_col_cp: "Controparte",
  db_active_col_stage: "Fase",
  db_active_col_next: "Prossima azione",
  db_active_col_attn: "Attenzione",
  db_active_next_intake: "Compila i dati",
  db_active_next_drafting: "Redigi documenti",
  db_active_next_qa: "Revisione e QA",
  db_active_next_delivery: "Consegna alla controparte",
  db_active_empty_title: "Nessuna trattativa attiva",
  db_active_empty_sub: "Avvia la prima trattativa per aprire un workspace transazionale.",
  db_active_empty_create: "Crea la prima trattativa",
  db_active_empty_example: "Vedi trattativa di esempio",
  db_recent_col_doc: "Documento",
  db_recent_col_ctx: "Trattativa / Controparte",
  db_recent_title: "Documenti recenti",
  db_recent_col_ver: "Versione",
  db_recent_col_updated: "Aggiornato",
  db_recent_open: "Apri",
  db_recent_continue: "Continua",
  db_recent_export: "Esporta",
  db_recent_placeholder_status: "Da completare",
  db_recent_ready_status: "Pronto",
  db_recent_empty_title: "Nessun documento",
  db_recent_empty_sub: "Crea un documento autonomo o apri una trattativa per iniziare.",
  db_membership_link: "Vai a Fatturazione e utilizzo",
  db_error_load: "Impossibile caricare i dati del workspace. Ricarica.",

  d_nav_documents: "Documenti",
  d_nav_documents_create: "Crea",
  d_nav_documents_review: "Revisiona",
  d_nav_documents_templates: "Modelli",
  d_nav_documents_files: "File",
  d_nav_language: "Lingua",
  d_nav_account: "Account",
  d_section_navigate: "Naviga",
  d_section_settings: "Impostazioni",

  // ============ Release 2.2B — IT ============
  dt_commodity_trade: "Compravendita di materie prime",
  dt_cross_border_supply: "Fornitura transfrontaliera",
  dt_shipping_chartering: "Shipping / Chartering",
  dt_trade_finance_prep: "Preparazione trade finance",
  dt_corporate_dd: "Corporate / Due Diligence",
  dt_other: "Altro",
  ds_initial_enquiry: "Richiesta iniziale",
  ds_counterparty_review: "Verifica controparte",
  ds_negotiation: "Negoziazione",
  ds_offer_quotation: "Offerta / Quotazione",
  ds_contract_preparation: "Preparazione del contratto",
  ds_payment_instrument: "Preparazione strumento di pagamento",
  ds_pre_shipment: "Pre-spedizione",
  ds_shipment: "Spedizione",
  ds_document_presentation: "Presentazione documenti",
  ds_completed: "Completato",
  ds_cancelled: "Annullato",
  dst_draft: "Bozza",
  dst_active: "Attiva",
  dst_on_hold: "In sospeso",
  dst_needs_attention: "Richiede attenzione",
  dst_completed: "Completata",
  dst_archived: "Archiviata",
  dst_closed: "Chiusa",
  pr_buyer: "Acquirente",
  pr_seller: "Venditore",
  pr_broker: "Broker",
  pr_intermediary: "Intermediario",
  pr_supplier: "Fornitore",
  pr_consignee: "Destinatario",
  pr_notify_party: "Notify party",
  pr_carrier: "Vettore",
  pr_charterer: "Noleggiatore",
  pr_shipowner: "Armatore",
  pr_bank: "Banca",
  pr_inspection_company: "Società di ispezione",
  pr_insurance_company: "Società di assicurazione",
  pr_other: "Altro",
  dw_page_title: "Nuova trattativa",
  dw_page_sub: "Workspace strutturato in sei passaggi.",
  dw_step1: "Panoramica",
  dw_step2: "Parti",
  dw_step3: "Merce e condizioni commerciali",
  dw_step4: "Consegna e spedizione",
  dw_step5: "Pagamento e banche",
  dw_step6: "Verifica e crea",
  dw_back: "Indietro",
  dw_next: "Continua",
  dw_save_draft: "Salva come bozza",
  dw_create: "Crea trattativa",
  dw_saving: "Salvataggio…",
  dw_unsaved_confirm: "Ci sono modifiche non salvate. Uscire comunque?",
  dw_title_lbl: "Titolo della trattativa",
  dw_reference_lbl: "Riferimento interno",
  dw_reference_hint: "Generato automaticamente. Modificabile. Deve essere univoco nel tuo account.",
  dw_type_lbl: "Tipo di trattativa",
  dw_owner_lbl: "Responsabile",
  dw_owner_you: "Tu",
  dw_description_lbl: "Descrizione",
  dw_internal_notes_lbl: "Note interne",
  dw_add_party: "Aggiungi parte",
  dw_party_role: "Ruolo",
  dw_party_company: "Società",
  dw_party_new_company: "+ Nuova società",
  dw_party_contact: "Contatto (opzionale)",
  dw_party_contact_none: "— Nessuno —",
  dw_party_notes: "Note",
  dw_party_remove: "Rimuovi parte",
  dw_party_empty: "Nessuna parte inserita. Aggiungi acquirente, venditore, broker o altri partecipanti.",
  dw_new_co_legal: "Ragione sociale",
  dw_new_co_type: "Tipo di controparte",
  dw_new_co_country: "Paese",
  dw_new_co_save: "Salva società",
  dw_new_co_cancel: "Annulla",
  dw_goods_commodity: "Prodotto / Materia prima",
  dw_goods_desc: "Descrizione",
  dw_goods_spec: "Specifica / grado / qualità",
  dw_goods_origin: "Origine",
  dw_goods_quantity: "Quantità",
  dw_goods_unit: "Unità",
  dw_goods_tolerance: "Tolleranza (± %)",
  dw_goods_unit_price: "Prezzo unitario",
  dw_goods_price_basis: "Base di prezzo",
  dw_goods_packaging: "Imballaggio",
  dw_goods_inspection: "Requisiti di ispezione",
  dw_goods_addl_terms: "Ulteriori condizioni commerciali",
  dw_currency: "Valuta",
  dw_total_value: "Valore totale",
  dw_total_calc: "Calcolato",
  dw_total_manual: "Sovrascrittura manuale",
  dw_total_recalc: "Ripristina calcolato",
  dw_delivery_incoterms: "Regola Incoterms",
  dw_delivery_incoterms_ver: "Versione Incoterms",
  dw_delivery_named_place: "Luogo / porto indicato",
  dw_delivery_country: "Paese di consegna",
  dw_delivery_load: "Luogo / porto di carico",
  dw_delivery_discharge: "Luogo / porto di scarico",
  dw_delivery_window: "Finestra di spedizione / consegna",
  dw_delivery_partial: "Spedizione parziale ammessa",
  dw_delivery_transship: "Trasbordo ammesso",
  dw_delivery_mode: "Modalità di trasporto",
  dw_delivery_vessel: "Dati nave / viaggio",
  dw_delivery_instructions: "Istruzioni di spedizione",
  dw_pay_method: "Metodo di pagamento",
  dw_pay_schedule: "Tempistica / calendario di pagamento",
  dw_pay_advance_pct: "Anticipo %",
  dw_pay_balance: "Condizioni del saldo",
  dw_pay_instrument: "Strumento bancario",
  dw_pay_issuing_bank: "Banca emittente",
  dw_pay_advising_bank: "Banca avvisante / ricevente",
  dw_pay_amount: "Importo dello strumento",
  dw_pay_currency: "Valuta dello strumento",
  dw_pay_validity: "Validità / scadenza",
  dw_pay_presentation: "Periodo di presentazione",
  dw_pay_confirmed: "Conferma richiesta",
  dw_pay_transferable: "Trasferibile",
  dw_pay_notes: "Note di pagamento",
  dw_review_title: "Verifica la trattativa",
  dw_review_sub: "Controlla le informazioni inserite. Puoi tornare a qualsiasi passaggio per correggere. Le informazioni mancanti resteranno evidenziate nel workspace.",
  dw_review_no_data: "Non indicato",
  dw_yes: "Sì",
  dw_no: "No",
  dl_col_reference: "Riferimento",
  dl_col_buyer: "Acquirente",
  dl_col_seller: "Venditore",
  dl_col_product: "Prodotto",
  dl_col_total: "Totale",
  dl_col_status: "Stato",
  dl_col_next_action: "Prossima azione",
  dl_filter_status: "Stato",
  dl_filter_stage: "Fase",
  dl_filter_all: "Tutti",
  dl_sort_reference: "Riferimento",
  dl_deals_new_btn: "Nuova trattativa",
  dl_include_archived: "Includi archiviate",
  mi_title: "Informazioni mancanti",
  mi_sub: "Elenco deterministico basato sui dati salvati. Completa gli elementi per ridurre il rischio prima della firma del contratto.",
  mi_ok: "Tutte le informazioni critiche sono presenti.",
  mi_no_title: "Titolo della trattativa",
  mi_no_type: "Tipo di trattativa",
  mi_no_buyer: "Acquirente",
  mi_no_seller: "Venditore",
  mi_no_product: "Prodotto / materia prima",
  mi_no_quantity: "Quantità",
  mi_no_currency: "Valuta",
  mi_no_incoterms: "Incoterms",
  mi_no_named_place: "Luogo / porto indicato",
  mi_no_delivery_window: "Finestra di spedizione",
  mi_no_payment_method: "Metodo di pagamento",
  mi_no_issuing_bank: "Banca emittente (LC)",
  mi_edit: "Correggi",
  na_title: "Prossima azione",
  na_none: "Nessuna prossima azione impostata.",
  na_action_lbl: "Azione",
  na_assignee_lbl: "Responsabile",
  na_due_lbl: "Scadenza",
  na_note_lbl: "Nota",
  na_save: "Salva",
  na_edit: "Modifica prossima azione",
  na_clear: "Rimuovi prossima azione",
  ws_tab_overview: "Panoramica",
  ws_tab_parties: "Parti",
  ws_tab_commercial: "Condizioni commerciali",
  ws_tab_delivery: "Consegna e spedizione",
  ws_tab_payment: "Pagamento e banche",
  ws_tab_documents: "Documenti",
  ws_tab_activity: "Attività",
  ws_tab_conversation: "Conversazione",
  conversation_title: "Conversazione della trattativa",
  conversation_sub: "Commenti, note interne, corrispondenza e attività di sistema in un unico flusso tracciabile.",
  conversation_kind_comment: "Commento",
  conversation_kind_note: "Nota interna",
  conversation_kind_correspondence: "Corrispondenza",
  conversation_outbound: "In uscita",
  conversation_inbound: "In entrata",
  conversation_subject: "Oggetto",
  conversation_from: "Da",
  conversation_to: "A",
  conversation_placeholder: "Scrivi un commento, una nota o una corrispondenza…",
  conversation_mentions_empty: "Aggiungi parti alla trattativa per abilitarne le menzioni.",
  conversation_send: "Aggiungi alla conversazione",
  conversation_stream: "Conversazione e attività",
  conversation_reply: "Rispondi",
  conversation_replying_to: "Risposta a",
  conversation_delete: "Elimina",
  conversation_delete_confirm: "Eliminare questa voce?",
  conversation_delete_blocked: "Elimina prima le relative risposte.",
  conversation_saved: "Aggiunto alla conversazione",
  conversation_deleted: "Voce eliminata",
  ws_stage_lbl: "Fase",
  ws_status_lbl: "Stato",
  ws_owner_lbl: "Responsabile",
  ws_responsible_lbl: "Responsabile",
  ws_responsible_none: "Non assegnato",
  ws_updated_lbl: "Ultimo aggiornamento",
  ws_edit: "Modifica",
  ws_save: "Salva modifiche",
  ws_saving: "Salvataggio…",
  ws_saved: "Salvato",
  ws_cancel_edit: "Annulla",
  ws_archive: "Archivia trattativa",
  ws_restore: "Ripristina trattativa",
  ws_delete: "Elimina definitivamente",
  ws_confirm_archive: "Archiviare questa trattativa? Sarà nascosta dagli elenchi attivi ma preservata.",
  ws_confirm_restore: "Ripristinare questa trattativa archiviata come attiva?",
  ws_confirm_delete: "Eliminare definitivamente? L'azione è irreversibile.",
  ws_delete_blocked: "Questa trattativa ha {n} documento/i collegato/i. Archivia oppure elimina prima i documenti.",
  ws_docs_empty: "Nessun documento ancora collegato a questa trattativa.",
  ws_docs_new: "Crea documento per questa trattativa",
  doc_pack_kicker: "Pacchetto consigliato",
  doc_pack_title: "Documenti suggeriti per questa trattativa",
  doc_pack_intro: "In base al tipo di trattativa, alla fase, ai termini di consegna e allo strumento di pagamento.",
  doc_pack_ready: "creati",
  doc_pack_create: "Crea",
  doc_pack_open: "Apri",
  doc_pack_reason_type: "Consigliato per questo tipo di trattativa",
  doc_pack_reason_stage: "Consigliato per la fase attuale",
  doc_pack_reason_payment: "Richiesto dallo strumento di pagamento selezionato",
  doc_pack_reason_shipping: "Consigliato per i termini di consegna selezionati",
  doc_pack_disclaimer: "Raccomandazione operativa. Verifica i requisiti contrattuali, bancari e normativi della transazione reale.",
  master_change_confirm: "Questa trattativa contiene già {count} documenti. Le modifiche ai dati principali potrebbero renderli obsoleti. Continuare?",
  master_stale_title: "{count} documenti da verificare",
  master_stale_intro: "I dati principali della trattativa sono cambiati dopo la creazione di questi documenti. Apri ogni documento, verifica i termini interessati e salvalo prima di proseguire con l'approvazione.",
  master_stale_badge: "Dati principali modificati",
  master_stale_workflow_blocked: "Verifica e salva il documento dopo la modifica dei dati della trattativa prima di proseguire con l'approvazione.",
  doc_status_draft: "Bozza",
  doc_status_in_review: "In revisione",
  doc_status_changes_requested: "Modifiche richieste",
  doc_status_approved: "Approvato",
  doc_status_final: "Finale",
  doc_action_submit: "Richiedi revisione",
  doc_action_approve: "Approva",
  doc_action_changes: "Richiedi modifiche",
  doc_action_final: "Segna come finale",
  doc_action_reopen: "Riapri",
  doc_workflow_changes_prompt: "Descrivi le modifiche richieste:",
  doc_workflow_note_prompt: "Nota facoltativa sulla decisione:",
  doc_workflow_updated: "Stato del documento aggiornato",
  doc_workflow_conflict: "Lo stato del documento è cambiato. Ricarica e riprova.",
  ws_activity_empty: "Nessuna attività registrata.",
  ws_ev_created: "Trattativa creata",
  ws_ev_stage: "Fase cambiata: {v}",
  ws_ev_status: "Stato cambiato: {v}",
  ws_ev_updated: "Dettagli aggiornati",
  ws_ev_next_action: "Prossima azione aggiornata",
  ws_ev_archived: "Trattativa archiviata",
  ws_ev_restored: "Trattativa ripristinata",
  ws_ev_draft_linked: "Documento {code} v{v} aggiunto",
  ws_ev_document_workflow: "Documento {code}: {status} · {actor}",
  err_reference_taken: "Questo riferimento è già usato da un'altra tua trattativa.",
  err_deal_has_documents: "Impossibile eliminare: la trattativa ha documenti collegati.",
  err_generic_save: "Salvataggio non riuscito. Riprova.",
  ok_deal_created: "Trattativa creata",
  ok_deal_updated: "Trattativa aggiornata",
  ok_deal_archived: "Trattativa archiviata",
  ok_deal_restored: "Trattativa ripristinata",
  ok_deal_deleted: "Trattativa eliminata",
  ok_draft_saved: "Bozza salvata",
};

const DE_OVERRIDES = {
  nav_product: "Produkt",
  nav_documents: "Dokumente",
  nav_how: "So funktioniert es",
  nav_use_cases: "Anwendungsfälle",
  nav_pricing: "Preise",
  nav_about: "Über uns",
  nav_contact: "Kontakt",
  nav_login: "Anmelden",
  nav_join: "3 Dokumente kostenlos",
  nav_workspace: "Mein Workspace",
  nav_logout: "Abmelden",
  nav_demo: "Demo ansehen",

  hero_label: "TRADEDOCS AI DESK",
  hero_title: "Professionelle Dokumente für internationale Geschäfte — erstellt in einem intelligenten Workspace.",
  hero_deal_line: "Käufer und Verkäufer. Ein gemeinsamer privater Deal-Raum.",
  hero_sub: "Erstellen Sie Handelsdokumente mit KI, teilen Sie sie mit Ihrem Geschäftspartner und besprechen Sie Änderungen in einem privaten Arbeitsbereich.",
  hero_cta_primary: "3 Dokumente kostenlos",
  hero_cta_secondary: "Plattform erkunden",
  hero_cta_login: "Anmelden",
  trust_1: "Internationale Handels-Workflows",
  trust_2: "Englisch, Italienisch, Deutsch, Spanisch, Chinesisch und Russisch",
  trust_3: "Bearbeitbare Ausgabe",
  trust_4: "Vertrauliche Behandlung",
  trust_5: "Optionale Experten-Review",
  hero_mockup_caption: "Von strukturierten Transaktionsdaten zu einem bearbeitbaren professionellen Dokument.",

  product_label: "DAS PRODUKT",
  product_title: "Mehr als eine Vorlagen-Bibliothek.",
  product_sub: "TradeDocs verbindet strukturierte Vorlagen, Transaktionsdaten und KI-gestütztes Drafting in einem professionellen Dokumenten-Workspace.",
  product_card_1_title: "Strukturierte Vorlagen",
  product_card_1_desc: "Vorlagen für reale Handels- und Schifffahrts-Workflows.",
  product_card_2_title: "Transaktionsbasiertes Drafting",
  product_card_2_desc: "Dokumente aus den konkreten Parametern der Transaktion.",
  product_card_3_title: "Dokumenten-Review",
  product_card_3_desc: "Kernklauseln, fehlende Angaben und mögliche Inkonsistenzen werden hervorgehoben.",
  product_card_4_title: "Wiederverwendbare Geschäftsdaten",
  product_card_4_desc: "Firmendaten, Gegenparteien und Transaktionsinfos für Folgedokumente gespeichert.",

  three_ways_title: "Starten Sie so, wie es für Sie passt.",
  three_ways_1_title: "Vorlage nutzen",
  three_ways_1_desc: "Vorlage auswählen, Transaktionsparameter eintragen, strukturierten Entwurf generieren.",
  three_ways_1_cta: "Vorlagen ansehen",
  three_ways_2_title: "Dokument hochladen",
  three_ways_2_desc: "Bestehendes Dokument hochladen — zusammenfassen, fehlende Punkte finden oder überarbeiten.",
  three_ways_2_cta: "Dokument prüfen",
  three_ways_3_title: "Anliegen beschreiben",
  three_ways_3_desc: "Beschreiben Sie Ihr Ziel — TradeDocs empfiehlt das passende Dokument oder den Workflow.",
  three_ways_3_cta: "Anliegen beschreiben",
  three_ways_3_badge: "Einfachster Einstieg",

  cats_title: "Dokumente für reale internationale Transaktionen.",
  cats_view: "Vorlagen ansehen",
  cat_commercial: "Handelsdokumente",
  cat_commercial_desc: "Eröffnungsschreiben, Corporate Offers und Geschäftskorrespondenz.",
  cat_trade_finance: "Trade Finance",
  cat_trade_finance_desc: "Bankschreiben, Akkreditive und Bereitschaftsprüfungen.",
  cat_shipping: "Schifffahrt & Chartering",
  cat_shipping_desc: "Charter-Kommunikation, Laytime, Ansprüche und Schiffsdaten.",
  cat_corporate: "Corporate & Due Diligence",
  cat_corporate_desc: "KYC, DD-Fragebögen und Corporate-Erklärungen.",
  cat_contracts: "Verträge & formelle Korrespondenz",
  cat_contracts_desc: "Liefervereinbarungen, Anzeigen, Änderungen und Begleitschreiben.",
  cat_management: "Management-Briefings",
  cat_management_desc: "Management-Zusammenfassungen und konkrete Empfehlungen für nächste Schritte.",

  preview_label: "INSIDE TRADEDOCS",
  preview_title: "Ein Workspace für Dokumente und Transaktionen.",
  preview_step_1: "Vorlage auswählen",
  preview_step_2: "Transaktionsparameter ergänzen",
  preview_step_3: "Belege hochladen",
  preview_step_4: "Professionellen Entwurf generieren",
  preview_step_5: "Fehlende Angaben prüfen",
  preview_step_6: "Dokument exportieren",

  how_title: "Von Transaktionsdaten zum fertigen Dokument.",
  how_1_t: "Wählen",
  how_1_d: "Vorlage auswählen, Datei hochladen oder gewünschtes Ergebnis beschreiben.",
  how_2_t: "Angaben ergänzen",
  how_2_d: "Parteien, Ware, Menge, Preis, Incoterms und weitere Details eintragen.",
  how_3_t: "Generieren",
  how_3_d: "TradeDocs strukturiert die Angaben und erstellt einen professionellen Entwurf.",
  how_4_t: "Prüfen",
  how_4_d: "Fehlende Details, Vorschläge und mögliche Inkonsistenzen prüfen.",
  how_5_t: "Exportieren",
  how_5_d: "Dokument speichern und in bearbeitbarem Format exportieren.",

  uc_title: "Für den praktischen kommerziellen Alltag gebaut.",
  uc_1_t: "Commodity Trading",
  uc_1_d: "LOI, Angebot, DD-Fragebogen und Follow-up-Korrespondenz vorbereiten.",
  uc_2_t: "Schifffahrt & Chartering",
  uc_2_d: "Charter-Fragen, Recaps, Ansprüche und Owner-Charterer-Korrespondenz.",
  uc_3_t: "Trade Finance",
  uc_3_d: "BCL-, SBLC- und LC-Entwürfe samt Dokumenten-Checklisten strukturieren.",
  uc_4_t: "Grenzüberschreitende Lieferung",
  uc_4_d: "Proforma, Liefervereinbarungen, Angebote und formelle Korrespondenz.",
  uc_5_t: "Gegenparteien-DD",
  uc_5_d: "Strukturierte KYC- und DD-Anfragen je nach Transaktionstyp.",
  uc_6_t: "Management-Reporting",
  uc_6_d: "Ein komplexes Dokumentenpaket in ein knappes Management-Summary überführen.",

  ai_title: "KI-gestütztes Drafting mit Transaktionslogik.",
  ai_sub: "TradeDocs hilft, Informationen zu strukturieren, Entwürfe vorzubereiten und Lücken zu identifizieren. Die Plattform ist auf Handels- und Schifffahrts-Workflows ausgelegt — kein Generic-Text.",
  ai_b_1: "Schnellere Erstentwürfe",
  ai_b_2: "Konsistente Struktur",
  ai_b_3: "Wiederverwendbare Firmendaten",
  ai_b_4: "Mehrsprachige Ausgabe",
  ai_b_5: "Bearbeitbare Dokumente",
  ai_b_6: "Prüfung auf fehlende Angaben",
  ai_b_7: "Optionaler Experten-Support",

  ps_title: "Brauchen Sie professionelle Unterstützung für eine komplexe Transaktion?",
  ps_sub: "Für komplexe Shipping-, Trade-Finance- oder Multi-Party-Vorgänge steht eine menschliche Review zur Verfügung.",
  ps_cta: "Experten-Support anfragen",

  conf_title: "Für vertrauliche Geschäftsdokumente ausgelegt.",
  conf_1: "Kontrollierter Zugriff",
  conf_2: "Geschützte Benutzerkonten",
  conf_3: "Vertrauliche Dateibehandlung",
  conf_4: "Keine öffentliche Nutzung von Kundendokumenten",
  conf_5: "NDA auf Anfrage",
  conf_6: "Optionen zur Dokumentlöschung",
  conf_7: "Organisationsbasierte Datentrennung",
  conf_8: "Klare Aufbewahrungsinformationen",

  mem_label: "MITGLIEDSCHAFT",
  mem_title: "Eine Mitgliedschaft für die tägliche internationale Dokumentation.",
  mem_sub: "Zugang zur Vorlagen-Bibliothek, den KI-Drafting-Tools, Dokumenten-Workflows und gespeicherten Transaktionsdaten mit einer monatlichen Mitgliedschaft.",
  mem_note: "Erstellen und exportieren Sie drei vollständige Dokumente ohne Karte und wählen Sie danach einen Monatstarif.",
  mem_expert: "Menschliche Experten-Review und maßgeschneiderter Support können separat bestellt werden.",

  ea_title: "Erstellen Sie heute Ihr erstes Handelsdokument.",
  ea_sub: "Richten Sie einen Workspace ein, wählen Sie eine professionelle Vorlage und exportieren Sie bis zu drei vollständige Dokumente ohne Karte.",
  ea_first: "Vorname",
  ea_last: "Nachname",
  ea_company: "Firma",
  ea_email: "Geschäftliche E-Mail",
  ea_country: "Land",
  ea_area: "Hauptarbeitsbereich",
  ea_lang: "Bevorzugte Sprache",
  ea_doc: "Wichtigster Dokumentenbedarf",
  ea_message: "Nachricht (optional)",
  ea_cta: "Kostenloses Konto erstellen",
  ea_ok: "Danke. Ihre Anfrage wurde empfangen.",
  ea_err: "Etwas ist schiefgegangen. Bitte erneut versuchen.",
  ea_privacy: "Mit dem Absenden bestätigen Sie, dass TradeDocs Sie bezüglich Ihrer Anfrage kontaktieren darf. Vertrauliche Behandlung.",

  ar_title: "Haben Sie ein Dokument oder eine Transaktion vorzubereiten?",
  ar_sub: "Kurzes Briefing senden — unser Desk meldet sich.",
  ar_cta: "Anfrage senden",
  ar_ok: "Danke. Wir melden uns innerhalb eines Werktages.",

  faq_title: "Häufige Fragen.",

  ft_product: "Produkt",
  ft_docs: "Dokumentbereiche",
  ft_company: "Unternehmen",
  ft_legal: "Rechtliches",
  ft_languages: "Sprachen",
  ft_rights: "Alle Rechte vorbehalten.",
  ft_tagline: "KI-gestützter Dokumenten-Workspace für internationalen Handel, Schifffahrt und Trade Finance.",

  // Client Portal — DE
  d_nav_dashboard: "Übersicht",
  d_nav_demo: "Demo ansehen",
  d_nav_deals: "Deals",
  d_nav_studio: "Dokumenten-Studio",
  d_nav_drafting: "Dokumenten-Studio",
  d_nav_review: "Dokumenten-Prüfung",
  d_nav_templates: "Vorlagen-Bibliothek",
  d_nav_files: "Dateien",
  d_nav_counterparties: "Gegenparteien",
  d_nav_company: "Firmenprofil",
  d_nav_expert: "Experten-Support",
  d_nav_requests: "Meine Anfragen",
  d_nav_intakes: "Meine Anfragen",
  d_nav_billing: "Abrechnung & Nutzung",
  d_nav_settings: "Einstellungen",
  d_nav_organization: "Organisation & Team",
  d_nav_logout: "Abmelden",
  d_section_workspace: "Workspace",
  d_section_business: "Geschäftsdaten",
  d_section_account: "Konto",
  db_welcome: "Willkommen zurück",
  db_welcome_sub: "Setzen Sie eine laufende Transaktion fort oder starten Sie ein neues Dokument.",
  db_qa_create: "Dokument erstellen",
  db_qa_create_sub: "Mit einer professionellen Vorlage starten.",
  db_qa_review: "Dokument prüfen",
  db_qa_review_sub: "Vorhandenes Dokument hochladen und analysieren.",
  db_qa_new_deal: "Neuen Deal starten",
  db_qa_new_deal_sub: "Neuen Transaktions-Workspace öffnen.",
  db_qa_describe: "Anliegen beschreiben",
  db_qa_describe_sub: "TradeDocs empfiehlt einen passenden Workflow.",
  db_section_active: "Aktive Deals",
  db_section_attention: "Dokumente, die Aufmerksamkeit brauchen",
  db_section_recent: "Aktuelle Dokumente",
  db_section_membership: "Mitgliedschaft & Nutzung",
  db_view_all: "Alle ansehen",
  db_empty_deals: "Keine aktiven Deals — starten Sie den ersten.",
  db_empty_attention: "Aktuell nichts zu tun.",
  db_empty_docs: "Noch keine Dokumente.",
  db_open: "Öffnen",
  db_plan_early: "Früher Zugang",
  db_renews: "Verlängerung",
  db_usage_docs: "Erzeugte Dokumente",
  db_usage_pages: "Verarbeitete Seiten",
  db_usage_deals: "Aktive Deals",
  db_usage_storage: "Speicher",
  db_coming_soon: "Bald verfügbar",
  db_coming_soon_sub: "Dieser Bereich wird für die nächste Client-Portal-Release vorbereitet.",
  db_notify: "Benachrichtigen, sobald verfügbar",
  d_deals_title: "Aktive Deals",
  d_deals_new: "Neuer Deal",
  d_deals_empty: "Noch keine Deals. Legen Sie den ersten an.",
  d_deals_kicker: "Arbeitsbereich",
  d_deals_loading: "Wird geladen…",
  d_col_title: "Titel",
  d_col_counterparty: "Gegenpartei",
  d_col_type: "Bereich",
  d_col_stage: "Phase",
  d_col_status: "Status",
  d_col_updated: "Aktualisiert",
  d_stage_intake: "Eingang",
  d_stage_drafting: "Entwurf",
  d_stage_qa: "Qualitätsprüfung",
  d_stage_delivery: "Übergabe",
  d_type_commercial: "Handelsentwurf",
  d_type_documentary: "Dokumentarische Unterstützung",
  d_type_shipping: "Shipping & Chartering",
  d_type_management: "Management-Desk",
  d_new_kicker: "Neuer Deal",
  d_new_title: "Neuen Deal öffnen",
  d_new_field_title: "Titel",
  d_new_field_counterparty: "Gegenpartei",
  d_new_field_type: "Bereich",
  d_new_field_value: "Wert",
  d_new_field_notes: "Notizen",
  d_new_value_ph: "z. B. USD 2,5 Mio.",
  d_new_submit: "Anlegen",
  d_new_close: "Schließen",
  d_toast_created: "Deal angelegt",
  d_toast_create_failed: "Deal konnte nicht angelegt werden",
  d_toast_stage_updated: "Phase aktualisiert",
  d_toast_failed: "Vorgang fehlgeschlagen",
  d_toast_deleted: "Gelöscht",
  d_deals_delete_action: "Löschen",
  dl_search_ph: "Deals durchsuchen…",
  dl_sort_updated: "Zuletzt aktualisiert",
  dl_sort_created: "Neueste",
  dl_sort_title: "Titel A→Z",
  dl_sort_stage: "Phase",
  dl_no_match: "Keine Deals passen zur Suche.",

  // Landing form options — DE
  ea_area_opt_1: "Internationaler Handel",
  ea_area_opt_2: "Commodity-Trading",
  ea_area_opt_3: "Shipping",
  ea_area_opt_4: "Chartering",
  ea_area_opt_5: "Trade Finance",
  ea_area_opt_6: "Import und Export",
  ea_area_opt_7: "Unternehmensdienste",
  ea_area_opt_8: "Sonstiges",
  ar_type_opt_1: "Handelsdokument",
  ar_type_opt_2: "Trade Finance",
  ar_type_opt_3: "Shipping / Chartering",
  ar_type_opt_4: "Corporate / DD",
  ar_type_opt_5: "Sonstiges",
  ar_urgency_1: "Normal",
  ar_urgency_2: "Hoch",
  ar_urgency_3: "Dringend",

  // Footer links — DE
  ft_link_overview: "Produktübersicht",
  ft_link_pricing: "Preise",
  ft_link_templates: "Vorlagen",
  ft_link_how: "So funktioniert es",
  ft_link_use_cases: "Anwendungsfälle",
  ft_link_early: "Kostenloses Konto",
  ft_link_login: "Anmelden",
  ft_link_about: "Über TradeDocs",
  ft_link_contact: "Kontakt",
  ft_link_support: "Experten-Support",
  ft_link_privacy: "Datenschutzerklärung",
  ft_link_terms: "Nutzungsbedingungen",
  ft_link_subscription: "Abonnement und Kündigung",
  ft_link_cookies: "Cookie-Richtlinie",
  ft_link_dp: "Auftragsverarbeitung",
  ft_link_nda: "NDA-Informationen",

  // FAQ — DE
  faq_q_1: "Was ist TradeDocs?",
  faq_a_1: "TradeDocs ist ein Dokumenten-Workspace für Fachleute des internationalen Handels. Er kombiniert strukturierte Vorlagen, Transaktionsdaten und KI-gestütztes Drafting, um Handels-, Schifffahrts- und Trade-Finance-Dokumente schneller und konsistenter zu erstellen.",
  faq_q_2: "Ist TradeDocs eine Anwaltskanzlei?",
  faq_a_2: "Nein. TradeDocs ist eine professionelle Dokumentenplattform. Es ist keine Anwaltskanzlei und leistet keine Rechtsberatung. Alle Ergebnisse sind Entwürfe, über die der Nutzer die volle Kontrolle behält.",
  faq_q_3: "Muss ich wissen, welche Vorlage ich verwenden soll?",
  faq_a_3: "Nicht zwingend. Sie können eine Ihnen bekannte Vorlage nutzen, ein bestehendes Dokument hochladen oder einfach beschreiben, was Sie brauchen — TradeDocs schlägt das passende Dokument oder den passenden Workflow vor.",
  faq_q_4: "Welche Dokumententypen sind verfügbar?",
  faq_a_4: "Handelsdokumente (LOI, ICPO, Angebote), Trade Finance (BCL, LC, SBLC), Schifffahrt und Chartering (Recaps, Laytime, Claims), Corporate & DD (KYC, DDQ) sowie Verträge und formelle Korrespondenz.",
  faq_q_5: "Kann ich ein bestehendes Dokument hochladen?",
  faq_a_5: "Ja. Sie können ein Dokument hochladen, um es zusammenzufassen, fehlende Informationen zu identifizieren oder eine überarbeitete Version zu erstellen.",
  faq_q_6: "Welche Sprachen werden unterstützt?",
  faq_a_6: "Die öffentliche Website und die Produktoberfläche sind auf Englisch, Italienisch und Deutsch verfügbar. Dokumente können abhängig von der Vorlage in mehreren Sprachen erzeugt und bearbeitet werden.",
  faq_q_7: "Kann ich Dokumente nach Word oder PDF exportieren?",
  faq_a_7: "Ja. Alle Entwürfe können in bearbeitbares Word (.docx) und PDF exportiert werden — für den sofortigen Einsatz oder für die weitere Bearbeitung außerhalb von TradeDocs.",
  faq_q_8: "Ist eine menschliche Expertenprüfung inbegriffen?",
  faq_a_8: "Automatisierte Prüfung ist inbegriffen. Menschliche Expertenprüfung und maßgeschneiderte Transaktionsunterstützung können für komplexe Aufgaben separat beauftragt werden.",
  faq_q_9: "Wie werden meine Informationen behandelt?",
  faq_a_9: "TradeDocs setzt Zugriffskontrolle, geschützte Nutzerkonten und organisationsbasierte Datentrennung ein. Kundendokumente werden für keinen öffentlichen Zweck verwendet. NDA auf Anfrage verfügbar.",
  faq_q_10: "Kann ich TradeDocs vor dem Abonnement testen?",
  faq_a_10: "Ja. Eine neue Organisation kann drei vollständige Dokumente ohne Karte erstellen, bearbeiten und exportieren. Für weitere Dokumente ist ein Monatsabonnement erforderlich.",
  faq_q_11: "Kann mein Unternehmen TradeDocs als Team nutzen?",
  faq_a_11: "Ja. TradeDocs unterstützt Unternehmenskonten, geteilte Vorlagen und geteilte Transaktions-Workspaces. Team-Funktionen werden vor dem öffentlichen Launch weiter ausgebaut.",

  // Auth — DE
  auth_signin: "Anmelden",
  auth_signup: "Konto erstellen",
  auth_email: "E-Mail",
  auth_password: "Passwort",
  auth_name: "Vollständiger Name",
  auth_company: "Unternehmen (optional)",
  auth_no_account: "Noch kein Konto?",
  auth_have_account: "Bereits registriert?",
  auth_login_btn: "Anmelden",
  auth_register_btn: "Registrieren",
  auth_err_login: "Ungültige E-Mail oder Passwort",
  auth_hero_title: "Der externe Desk, den Ihre Deals verdienen.",
  auth_hero_sub: "Melden Sie sich an — Deals, Entwürfe und Vorlagen-Bibliothek.",
  auth_welcome_back: "Willkommen zurück.",
  auth_open_desk: "Öffnen Sie Ihren Desk.",
  auth_trust_line: "Vertraulich · NDA · Verschlüsselt",
  auth_loading: "…",
  auth_forgot_password: "Passwort vergessen?",
  auth_privacy_signup: "Mit der Kontoerstellung bestätigen Sie, die Informationen zur Verarbeitung personenbezogener Daten zur Kenntnis genommen zu haben:",
  auth_privacy_signin: "Informationen zur Verarbeitung personenbezogener Daten:",

  // Studio — DE
  cp_intro: "Einmal gespeichert, überall wiederverwendet. Rechtliche Briefköpfe, Unterzeichner und Bankdaten werden automatisch in generierte Dokumente eingefügt.",
  cp_sec_legal: "Rechtliche Identität",
  cp_sec_contact: "Kontakt",
  cp_sec_defaults: "Standardeinstellungen",
  cp_sec_signatories: "Unterzeichner",
  cp_sec_bank: "Bank & Fußzeile",
  cp_f_legal_name: "Firmenname (rechtlich)",
  cp_f_country: "Land",
  cp_f_registration: "Handelsregisternummer",
  cp_f_vat: "USt-IdNr. / Steuernummer",
  cp_f_registered_addr: "Eingetragene Anschrift",
  cp_f_operational_addr: "Geschäftsanschrift",
  cp_f_website: "Website",
  cp_f_phone: "Telefon",
  cp_f_email: "Öffentliche E-Mail",
  cp_f_currency: "Standardwährung",
  cp_f_lang: "Standard-Dokumentsprache",
  cp_f_name: "Name",
  cp_f_title: "Titel",
  cp_f_authority: "Vertretungsbefugnis",
  cp_auth_sole: "Einzeln",
  cp_auth_joint: "Gemeinsam",
  cp_auth_advisory: "Beratend",
  cp_add_sign: "Unterzeichner hinzufügen",
  cp_f_bank: "Bankverbindung",
  cp_f_bank_help: "Korrespondenzbank, Konto, SWIFT, IBAN — dieser Block wird in Rechnungen, LC-Anträge und Zahlungsanweisungen eingefügt.",
  cp_f_footer: "Fußzeile / Signaturblock",
  cp_f_footer_help: "Kurzer Fußzeilentext für Briefe — z. B. 'Verwaltet von AIT Technologies GmbH · HRB 12345'.",
  cp_save_btn: "Firmenprofil speichern",
  cp_saving: "Wird gespeichert…",
  cp_saved: "Firmenprofil · gespeichert",
  cp_save_failed: "Speichern fehlgeschlagen",
  cp_loading: "Wird geladen…",

  ctp_intro: "Verzeichnis von Käufern, Verkäufern, Banken, Reedern und Agenten. Verknüpfte Gegenparteien füllen Deal-Parteien automatisch aus.",
  ctp_new_btn: "Neue Gegenpartei",
  ctp_search_ph: "Suche nach Name, Land oder Typ…",
  ctp_empty: "Noch keine Gegenparteien — legen Sie den ersten Käufer, Verkäufer oder eine Bank an.",
  ctp_no_matches: "Keine Treffer für diese Suche.",
  ctp_new_kicker: "Neue Gegenpartei",
  ctp_edit_kicker: "Gegenpartei bearbeiten",
  ctp_untitled: "Ohne Titel",
  ctp_f_legal_name: "Firmenname *",
  ctp_f_type: "Typ",
  ctp_f_country: "Land",
  ctp_f_vat: "USt-IdNr. / Steuernummer",
  ctp_f_address: "Eingetragene Anschrift",
  ctp_f_risk: "Risikostatus",
  ctp_f_website: "Website",
  ctp_f_contacts: "Kontakte",
  ctp_add_contact: "Kontakt hinzufügen",
  ctp_contact_name: "Name",
  ctp_contact_role: "Rolle",
  ctp_contact_email: "E-Mail",
  ctp_contact_phone: "Telefon",
  ctp_f_bank: "Bankverbindung",
  ctp_f_notes: "Interne Notizen",
  ctp_risk_std: "Standard",
  ctp_risk_watch: "Beobachtung",
  ctp_risk_blocked: "Gesperrt",
  ctp_edit_btn: "Bearbeiten",
  ctp_save_btn: "Gegenpartei speichern",
  ctp_cancel_btn: "Abbrechen",
  ctp_confirm_delete: "Diese Gegenpartei löschen?",
  ctp_load_failed: "Laden fehlgeschlagen",
  ctp_delete_failed: "Löschen fehlgeschlagen",
  ctp_deleted: "Gelöscht",
  ctp_saved: "Gespeichert",
  ctp_legal_required: "Firmenname ist erforderlich",

  dd_sec_parties: "Parteien",
  dd_sec_product: "Produkt",
  dd_sec_commercial: "Handelsbedingungen",
  dd_sec_delivery: "Lieferung",
  dd_sec_payment: "Zahlung",
  dd_sec_inspection: "Prüfung & Qualität",
  dd_f_buyer_name: "Käufer (Firmenname)",
  dd_f_seller_name: "Verkäufer (Firmenname)",
  dd_f_seller_country: "Land des Verkäufers",
  dd_f_buyer_country: "Land des Käufers",
  dd_f_role: "Ihre Rolle",
  dd_f_commodity: "Ware",
  dd_f_grade: "Grade / Spezifikation",
  dd_f_origin: "Ursprung",
  dd_f_quantity: "Menge",
  dd_f_price: "Preis / Formel",
  dd_f_currency: "Währung",
  dd_f_incoterms: "Incoterms",
  dd_f_total: "Gesamtvertragswert",
  dd_f_load_port: "Ladehafen",
  dd_f_discharge_port: "Löschhafen",
  dd_f_shipment: "Verschiffungs- / Lieferzeitraum",
  dd_f_vessel: "Schiff / Transportart",
  dd_f_payment_instr: "Zahlungsinstrument",
  dd_f_issuing_bank: "Ausstellende Bank",
  dd_f_advising_bank: "Avisierende Bank",
  dd_f_payment_notes: "Anmerkungen zu Zahlungsbedingungen",
  dd_f_inspection_agency: "Prüfstelle",
  dd_f_insurance: "Versicherungsbedingungen",
  dd_f_law: "Anwendbares Recht",
  dd_f_arbitration: "Schiedsgericht (Sitz)",
  dd_intro_data: "Strukturierte Felder zum Deal. Diese Werte füllen passende Parameter in jedem Dokument aus, das aus diesem Workspace erstellt wird.",
  dd_save_data: "Speichern",
  dd_saving_data: "Wird gespeichert…",
  dd_saved_data: "Deal-Daten gespeichert",

  st_mode_create: "Erstellen",
  st_mode_review: "Prüfen",
  st_mode_improve: "Verbessern",
  st_mode_translate: "Übersetzen",
  st_mode_compare: "Vergleichen",
  st_mode_adapt: "Anpassen",
  st_mode_reply: "Antwort verfassen",
  st_help_create: "Erzeugt ein neues Dokument aus Vorlage + Parametern.",
  st_help_review: "Analysiert ein bestehendes Dokument — Zusammenfassung, fehlende Angaben, Warnsignale.",
  st_help_improve: "Präzisiert die Sprache, hebt den Ton, entfernt Mehrdeutigkeiten.",
  st_help_translate: "Übersetzt das Quelldokument in die Zielsprache.",
  st_help_compare: "Vergleicht zwei Versionen eines Dokuments (A und B einfügen).",
  st_prefilled_from_deal: "{count} Felder aus den zentralen Deal-Daten ausgefüllt",
  st_help_adapt: "Passt ein bestehendes Dokument an neue Parteien/Bedingungen an.",
  st_help_reply: "Verfasst eine professionelle Antwort auf eine erhaltene Nachricht.",
  st_risk_prefix: "Risiko",
  st_risk_low: "niedrig",
  st_risk_medium: "mittel",
  st_risk_high: "hoch",
  st_intro: "Handelsdokumente mit KI erstellen, prüfen, übersetzen, anpassen und verbessern. Vorlage, Modus und Sprache wählen — dann erzeugen.",
  st_doc_title: "Dokumententitel (optional)",
  st_doc_title_ph: "z. B. LC-Änderung Follow-up #2",
  st_source_label: "Quelldokument / Nachricht",
  st_source_ph: "Fügen Sie hier den Dokumenttext ein, den Sie prüfen, verbessern, übersetzen, anpassen oder beantworten möchten…",
  st_missing_required: "Pflichtfelder fehlen",
  st_source_required_toast: "Bitte fügen Sie das Quelldokument für diesen Modus ein.",
  st_draft_ready: "Entwurf fertig",
  st_draft_failed: "Erzeugung fehlgeschlagen",
  st_stop: "Stopp",
  st_archive_word: "Word in Dateien speichern",
  st_archive_pdf: "PDF in Dateien speichern",
  st_archiving: "Version wird gespeichert…",
  st_archived: "Unveränderliche {format}-Version {version} in Dateien gespeichert",
  st_archive_failed: "Export konnte nicht in Dateien gespeichert werden",
  st_immutable: "Diese archivierte Version ist unveränderlich",

  // Release 2.2A — DE
  db_hero_kicker: "Arbeitsbereich",
  db_hero_title: "Wo Deals zu Dokumenten werden.",
  db_primary_cta: "Neue Transaktion starten",
  db_secondary_doc: "Eigenständiges Dokument erstellen",
  db_secondary_review: "Bestehendes Dokument prüfen",
  db_secondary_ask: "TradeDocs fragen",
  db_check_title: "Arbeitsbereich einrichten",
  db_check_sub: "Sechs einmalige Schritte, die alle Workflows freischalten.",
  db_check_progress: "{done} von {total} erledigt",
  db_check_1: "Firmenprofil vervollständigen",
  db_check_2: "Autorisierte Unterzeichner hinzufügen",
  db_check_3: "Bankdaten hinzufügen",
  db_check_4: "Erste Gegenpartei anlegen",
  db_check_5: "Ersten Deal starten",
  db_check_6: "Erstes Dokument erstellen",
  db_continue_title: "Weiterarbeiten",
  db_continue_empty: "Aktuell nichts in Arbeit.",
  db_attention_title: "Benötigt Aufmerksamkeit",
  db_attention_none: "Aktuell nichts zu tun.",
  db_attention_intake: "Deal im Intake — Kerninformationen ergänzen",
  db_attention_qa: "Deal in der QA — vor Auslieferung prüfen",
  db_attention_incomplete: "Deal-Parameter unvollständig",
  db_attention_placeholder: "Entwurf enthält Platzhalter in Klammern",
  db_att_open: "Öffnen",
  db_active_title: "Aktive Deals",
  db_active_col_deal: "Deal",
  db_active_col_cp: "Gegenpartei",
  db_active_col_stage: "Phase",
  db_active_col_next: "Nächster Schritt",
  db_active_col_attn: "Achtung",
  db_active_next_intake: "Deal-Daten ausfüllen",
  db_active_next_drafting: "Dokumente entwerfen",
  db_active_next_qa: "Prüfung & QA",
  db_active_next_delivery: "An Gegenpartei liefern",
  db_active_empty_title: "Noch keine aktiven Deals",
  db_active_empty_sub: "Starten Sie Ihren ersten Deal, um einen Transaktions-Workspace zu öffnen.",
  db_active_empty_create: "Ersten Deal anlegen",
  db_active_empty_example: "Beispiel-Deal ansehen",
  db_recent_col_doc: "Dokument",
  db_recent_col_ctx: "Deal / Gegenpartei",
  db_recent_title: "Aktuelle Dokumente",
  db_recent_col_ver: "Version",
  db_recent_col_updated: "Aktualisiert",
  db_recent_open: "Öffnen",
  db_recent_continue: "Weiter",
  db_recent_export: "Exportieren",
  db_recent_placeholder_status: "Platzhalter",
  db_recent_ready_status: "Bereit",
  db_recent_empty_title: "Noch keine Dokumente",
  db_recent_empty_sub: "Erstellen Sie ein eigenständiges Dokument oder öffnen Sie einen Deal, um zu starten.",
  db_membership_link: "Zu Abrechnung & Nutzung",
  db_error_load: "Workspace-Daten konnten nicht geladen werden. Bitte neu laden.",

  d_nav_documents: "Dokumente",
  d_nav_documents_create: "Erstellen",
  d_nav_documents_review: "Prüfen",
  d_nav_documents_templates: "Vorlagen",
  d_nav_documents_files: "Dateien",
  d_nav_language: "Sprache",
  d_nav_account: "Konto",
  d_section_navigate: "Navigieren",
  d_section_settings: "Einstellungen",

  // ============ Release 2.2B — DE ============
  dt_commodity_trade: "Warenhandel",
  dt_cross_border_supply: "Grenzüberschreitende Lieferung",
  dt_shipping_chartering: "Shipping / Chartering",
  dt_trade_finance_prep: "Trade-Finance-Vorbereitung",
  dt_corporate_dd: "Corporate / Due Diligence",
  dt_other: "Sonstiges",
  ds_initial_enquiry: "Erste Anfrage",
  ds_counterparty_review: "Gegenpartei-Prüfung",
  ds_negotiation: "Verhandlung",
  ds_offer_quotation: "Angebot / Quotierung",
  ds_contract_preparation: "Vertragsvorbereitung",
  ds_payment_instrument: "Zahlungsinstrument-Vorbereitung",
  ds_pre_shipment: "Vor der Verladung",
  ds_shipment: "Verladung",
  ds_document_presentation: "Dokumentenvorlage",
  ds_completed: "Abgeschlossen",
  ds_cancelled: "Storniert",
  dst_draft: "Entwurf",
  dst_active: "Aktiv",
  dst_on_hold: "Ausgesetzt",
  dst_needs_attention: "Braucht Aufmerksamkeit",
  dst_completed: "Abgeschlossen",
  dst_archived: "Archiviert",
  dst_closed: "Geschlossen",
  pr_buyer: "Käufer",
  pr_seller: "Verkäufer",
  pr_broker: "Makler",
  pr_intermediary: "Vermittler",
  pr_supplier: "Lieferant",
  pr_consignee: "Empfänger",
  pr_notify_party: "Notify Party",
  pr_carrier: "Frachtführer",
  pr_charterer: "Charterer",
  pr_shipowner: "Reeder",
  pr_bank: "Bank",
  pr_inspection_company: "Prüfgesellschaft",
  pr_insurance_company: "Versicherungsgesellschaft",
  pr_other: "Sonstiges",
  dw_page_title: "Neuer Deal",
  dw_page_sub: "Strukturierter Workspace in sechs Schritten.",
  dw_step1: "Übersicht",
  dw_step2: "Parteien",
  dw_step3: "Ware & Kommerzielle Bedingungen",
  dw_step4: "Lieferung & Versand",
  dw_step5: "Zahlung & Bank",
  dw_step6: "Prüfen & Anlegen",
  dw_back: "Zurück",
  dw_next: "Weiter",
  dw_save_draft: "Als Entwurf speichern",
  dw_create: "Deal anlegen",
  dw_saving: "Speichern…",
  dw_unsaved_confirm: "Es gibt ungespeicherte Änderungen. Trotzdem verlassen?",
  dw_title_lbl: "Titel des Deals",
  dw_reference_lbl: "Interne Referenz",
  dw_reference_hint: "Automatisch erzeugt. Bearbeitbar. Muss in Ihrem Konto eindeutig sein.",
  dw_type_lbl: "Deal-Typ",
  dw_owner_lbl: "Verantwortlich",
  dw_owner_you: "Sie",
  dw_description_lbl: "Beschreibung",
  dw_internal_notes_lbl: "Interne Notizen",
  dw_add_party: "Partei hinzufügen",
  dw_party_role: "Rolle",
  dw_party_company: "Firma",
  dw_party_new_company: "+ Neue Firma",
  dw_party_contact: "Ansprechperson (optional)",
  dw_party_contact_none: "— Keine —",
  dw_party_notes: "Notizen",
  dw_party_remove: "Partei entfernen",
  dw_party_empty: "Noch keine Parteien. Käufer, Verkäufer, Makler oder weitere Beteiligte hinzufügen.",
  dw_new_co_legal: "Firmenname (rechtlich)",
  dw_new_co_type: "Gegenparteityp",
  dw_new_co_country: "Land",
  dw_new_co_save: "Firma speichern",
  dw_new_co_cancel: "Abbrechen",
  dw_goods_commodity: "Produkt / Ware",
  dw_goods_desc: "Beschreibung",
  dw_goods_spec: "Spezifikation / Grade / Qualität",
  dw_goods_origin: "Herkunft",
  dw_goods_quantity: "Menge",
  dw_goods_unit: "Einheit",
  dw_goods_tolerance: "Toleranz (± %)",
  dw_goods_unit_price: "Stückpreis",
  dw_goods_price_basis: "Preisbasis",
  dw_goods_packaging: "Verpackung",
  dw_goods_inspection: "Inspektionsanforderungen",
  dw_goods_addl_terms: "Weitere kommerzielle Bedingungen",
  dw_currency: "Währung",
  dw_total_value: "Gesamtwert",
  dw_total_calc: "Berechnet",
  dw_total_manual: "Manuelle Überschreibung",
  dw_total_recalc: "Auf berechnet zurücksetzen",
  dw_delivery_incoterms: "Incoterms-Regel",
  dw_delivery_incoterms_ver: "Incoterms-Version",
  dw_delivery_named_place: "Genannter Ort / Hafen",
  dw_delivery_country: "Lieferland",
  dw_delivery_load: "Verladehafen / -ort",
  dw_delivery_discharge: "Entladehafen / -ort",
  dw_delivery_window: "Versand- / Lieferfenster",
  dw_delivery_partial: "Teilverladung zulässig",
  dw_delivery_transship: "Umschlag zulässig",
  dw_delivery_mode: "Transportart",
  dw_delivery_vessel: "Schiff / Reise-Details",
  dw_delivery_instructions: "Versandanweisungen",
  dw_pay_method: "Zahlungsart",
  dw_pay_schedule: "Zahlungszeitplan",
  dw_pay_advance_pct: "Anzahlung %",
  dw_pay_balance: "Restzahlungsbedingungen",
  dw_pay_instrument: "Bankinstrument",
  dw_pay_issuing_bank: "Eröffnungsbank",
  dw_pay_advising_bank: "Avisier- / Empfängerbank",
  dw_pay_amount: "Instrumentbetrag",
  dw_pay_currency: "Instrumentwährung",
  dw_pay_validity: "Gültigkeit / Ablauf",
  dw_pay_presentation: "Vorlagefrist",
  dw_pay_confirmed: "Bestätigung erforderlich",
  dw_pay_transferable: "Übertragbar",
  dw_pay_notes: "Zahlungshinweise",
  dw_review_title: "Deal prüfen",
  dw_review_sub: "Überprüfen Sie die eingegebenen Angaben. Sie können jederzeit zu einem Schritt zurück. Fehlende Angaben werden im Workspace weiterhin markiert.",
  dw_review_no_data: "Keine Angabe",
  dw_yes: "Ja",
  dw_no: "Nein",
  dl_col_reference: "Referenz",
  dl_col_buyer: "Käufer",
  dl_col_seller: "Verkäufer",
  dl_col_product: "Produkt",
  dl_col_total: "Gesamt",
  dl_col_status: "Status",
  dl_col_next_action: "Nächste Aktion",
  dl_filter_status: "Status",
  dl_filter_stage: "Phase",
  dl_filter_all: "Alle",
  dl_sort_reference: "Referenz",
  dl_deals_new_btn: "Neuer Deal",
  dl_include_archived: "Archivierte einschließen",
  mi_title: "Fehlende Informationen",
  mi_sub: "Deterministische Checkliste basierend auf gespeicherten Daten. Vervollständigen Sie die Punkte, um Risiken vor Vertragsabschluss zu senken.",
  mi_ok: "Alle kritischen Informationen liegen vor.",
  mi_no_title: "Deal-Titel",
  mi_no_type: "Deal-Typ",
  mi_no_buyer: "Käufer",
  mi_no_seller: "Verkäufer",
  mi_no_product: "Produkt / Ware",
  mi_no_quantity: "Menge",
  mi_no_currency: "Währung",
  mi_no_incoterms: "Incoterms",
  mi_no_named_place: "Genannter Ort / Hafen",
  mi_no_delivery_window: "Versandfenster",
  mi_no_payment_method: "Zahlungsart",
  mi_no_issuing_bank: "Eröffnungsbank (LC)",
  mi_edit: "Korrigieren",
  na_title: "Nächste Aktion",
  na_none: "Keine nächste Aktion gesetzt.",
  na_action_lbl: "Aktion",
  na_assignee_lbl: "Verantwortlich",
  na_due_lbl: "Fällig am",
  na_note_lbl: "Notiz",
  na_save: "Speichern",
  na_edit: "Nächste Aktion bearbeiten",
  na_clear: "Nächste Aktion entfernen",
  ws_tab_overview: "Übersicht",
  ws_tab_parties: "Parteien",
  ws_tab_commercial: "Kommerzielle Bedingungen",
  ws_tab_delivery: "Lieferung & Versand",
  ws_tab_payment: "Zahlung & Bank",
  ws_tab_documents: "Dokumente",
  ws_tab_activity: "Aktivität",
  ws_tab_conversation: "Konversation",
  conversation_title: "Deal-Konversation",
  conversation_sub: "Kommentare, interne Notizen, Korrespondenz und Systemaktivitäten in einem nachvollziehbaren Verlauf.",
  conversation_kind_comment: "Kommentar",
  conversation_kind_note: "Interne Notiz",
  conversation_kind_correspondence: "Korrespondenz",
  conversation_outbound: "Ausgehend",
  conversation_inbound: "Eingehend",
  conversation_subject: "Betreff",
  conversation_from: "Von",
  conversation_to: "An",
  conversation_placeholder: "Kommentar, Notiz oder Korrespondenz erfassen…",
  conversation_mentions_empty: "Deal-Parteien hinzufügen, um Erwähnungen zu aktivieren.",
  conversation_send: "Zur Konversation hinzufügen",
  conversation_stream: "Konversation & Aktivität",
  conversation_reply: "Antworten",
  conversation_replying_to: "Antwort auf",
  conversation_delete: "Löschen",
  conversation_delete_confirm: "Diesen Eintrag löschen?",
  conversation_delete_blocked: "Zuerst die Antworten löschen.",
  conversation_saved: "Zur Konversation hinzugefügt",
  conversation_deleted: "Eintrag gelöscht",
  ws_stage_lbl: "Phase",
  ws_status_lbl: "Status",
  ws_owner_lbl: "Verantwortlich",
  ws_responsible_lbl: "Verantwortlich",
  ws_responsible_none: "Nicht zugewiesen",
  ws_updated_lbl: "Letzte Aktualisierung",
  ws_edit: "Bearbeiten",
  ws_save: "Änderungen speichern",
  ws_saving: "Speichern…",
  ws_saved: "Gespeichert",
  ws_cancel_edit: "Abbrechen",
  ws_archive: "Deal archivieren",
  ws_restore: "Deal wiederherstellen",
  ws_delete: "Endgültig löschen",
  ws_confirm_archive: "Diesen Deal archivieren? Er wird aus aktiven Listen ausgeblendet, aber erhalten.",
  ws_confirm_restore: "Diesen archivierten Deal als aktiv wiederherstellen?",
  ws_confirm_delete: "Endgültig löschen? Dies kann nicht rückgängig gemacht werden.",
  ws_delete_blocked: "Dieser Deal hat {n} verknüpfte(s) Dokument(e). Archivieren Sie ihn oder löschen Sie zuerst die Dokumente.",
  ws_docs_empty: "Noch keine Dokumente mit diesem Deal verknüpft.",
  ws_docs_new: "Dokument für diesen Deal erstellen",
  doc_pack_kicker: "Empfohlenes Paket",
  doc_pack_title: "Für diesen Deal vorgeschlagene Dokumente",
  doc_pack_intro: "Basierend auf Deal-Typ, aktueller Phase, Lieferbedingungen und Zahlungsinstrument.",
  doc_pack_ready: "erstellt",
  doc_pack_create: "Erstellen",
  doc_pack_open: "Öffnen",
  doc_pack_reason_type: "Für diesen Deal-Typ empfohlen",
  doc_pack_reason_stage: "Für die aktuelle Phase empfohlen",
  doc_pack_reason_payment: "Vom gewählten Zahlungsinstrument benötigt",
  doc_pack_reason_shipping: "Für die gewählten Lieferbedingungen empfohlen",
  doc_pack_disclaimer: "Operative Empfehlung. Vertragliche, bankseitige und regulatorische Anforderungen der konkreten Transaktion sind gesondert zu prüfen.",
  master_change_confirm: "Dieser Deal enthält bereits {count} Dokument(e). Änderungen an den Stammdaten können sie veralten lassen. Fortfahren?",
  master_stale_title: "{count} Dokument(e) müssen geprüft werden",
  master_stale_intro: "Die Deal-Stammdaten wurden nach Erstellung dieser Dokumente geändert. Öffnen, prüfen und speichern Sie jedes betroffene Dokument, bevor die Freigabe fortgesetzt wird.",
  master_stale_badge: "Stammdaten geändert",
  master_stale_workflow_blocked: "Prüfen und speichern Sie dieses Dokument nach der Änderung der Deal-Daten, bevor die Freigabe fortgesetzt wird.",
  doc_status_draft: "Entwurf",
  doc_status_in_review: "In Prüfung",
  doc_status_changes_requested: "Änderungen angefordert",
  doc_status_approved: "Freigegeben",
  doc_status_final: "Final",
  doc_action_submit: "Prüfung anfordern",
  doc_action_approve: "Freigeben",
  doc_action_changes: "Änderungen anfordern",
  doc_action_final: "Als final markieren",
  doc_action_reopen: "Wieder öffnen",
  doc_workflow_changes_prompt: "Erforderliche Änderungen beschreiben:",
  doc_workflow_note_prompt: "Optionale Entscheidungsnotiz:",
  doc_workflow_updated: "Dokumentstatus aktualisiert",
  doc_workflow_conflict: "Der Dokumentstatus wurde geändert. Neu laden und erneut versuchen.",
  ws_activity_empty: "Noch keine Aktivität erfasst.",
  ws_ev_created: "Deal angelegt",
  ws_ev_stage: "Phase geändert: {v}",
  ws_ev_status: "Status geändert: {v}",
  ws_ev_updated: "Details aktualisiert",
  ws_ev_next_action: "Nächste Aktion aktualisiert",
  ws_ev_archived: "Deal archiviert",
  ws_ev_restored: "Deal wiederhergestellt",
  ws_ev_draft_linked: "Dokument {code} v{v} hinzugefügt",
  ws_ev_document_workflow: "Dokument {code}: {status} · {actor}",
  err_reference_taken: "Diese Referenz wird bereits von einem anderen Ihrer Deals verwendet.",
  err_deal_has_documents: "Löschen nicht möglich: dem Deal sind Dokumente zugeordnet.",
  err_generic_save: "Speichern fehlgeschlagen. Bitte erneut versuchen.",
  ok_deal_created: "Deal angelegt",
  ok_deal_updated: "Deal aktualisiert",
  ok_deal_archived: "Deal archiviert",
  ok_deal_restored: "Deal wiederhergestellt",
  ok_deal_deleted: "Deal gelöscht",
  ok_draft_saved: "Entwurf gespeichert",
};

const DICT = {
  en: EN,
  it: { ...EN, ...IT_OVERRIDES, ...IT_WORKSPACE_MISSING },
  de: { ...EN, ...DE_OVERRIDES, ...DE_WORKSPACE_MISSING },
  es: { ...EN, ...ES_PUBLIC, ...ES_WORKSPACE, ...CONVERSION_LOCALES.es.auth },
  zh: { ...EN, ...ZH_PUBLIC, ...ZH_WORKSPACE, ...CONVERSION_LOCALES.zh.auth },
  ru: { ...EN, ...RU_PUBLIC, ...RU_WORKSPACE, ...CONVERSION_LOCALES.ru.auth },
};

export const SUPPORTED_LANGS = ["en", "it", "de", "es", "zh", "ru"];

// Detects the best matching supported language from browser preferences.
// Priority: URL ?lang=xx → localStorage → navigator.languages → "en".
function detectBrowserLang() {
  if (typeof window !== "undefined") {
    try {
      const q = new URLSearchParams(window.location.search).get("lang");
      if (q && SUPPORTED_LANGS.includes(q.toLowerCase())) return q.toLowerCase();
    } catch (e) { /* no-op */ }
  }
  if (typeof navigator === "undefined") return "en";
  const list = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || "en"];
  for (const raw of list) {
    if (!raw) continue;
    const primary = String(raw).toLowerCase().split("-")[0];
    if (SUPPORTED_LANGS.includes(primary)) return primary;
  }
  return "en";
}

// Localised <html lang>, <title>, <meta description> and og:locale.
// Updated inside I18nProvider on every language change so that search engines,
// browser assistive tech and social share previews stay in sync with the UI.
const META = {
  en: {
    html: "en",
    ogLocale: "en_US",
    title: "TradeDocs AI Desk — AI-assisted document workspace for international trade",
    description: "TradeDocs AI Desk — professional documents for international transactions. Structured templates, AI-assisted drafting and transaction workspace for commercial, shipping and trade-finance workflows.",
  },
  it: {
    html: "it",
    ogLocale: "it_IT",
    title: "TradeDocs AI Desk — Spazio di lavoro documentale con AI per il commercio internazionale",
    description: "TradeDocs AI Desk — documenti professionali per transazioni internazionali. Modelli strutturati, redazione assistita da AI e spazio di lavoro transazionale per commerciale, shipping e trade finance.",
  },
  de: {
    html: "de",
    ogLocale: "de_DE",
    title: "TradeDocs AI Desk — KI-gestützter Dokumenten-Workspace für den internationalen Handel",
    description: "TradeDocs AI Desk — professionelle Dokumente für internationale Transaktionen. Strukturierte Vorlagen, KI-gestütztes Drafting und Transaktions-Workspace für Handel, Schifffahrt und Trade Finance.",
  },
  es: {
    html: "es",
    ogLocale: "es_ES",
    title: "TradeDocs AI Desk — Espacio documental con IA para el comercio internacional",
    description: "Documentos profesionales para transacciones internacionales, con plantillas estructuradas, redacción asistida por IA y un espacio de trabajo para operaciones comerciales.",
  },
  zh: {
    html: "zh-CN",
    ogLocale: "zh_CN",
    title: "TradeDocs AI Desk — 面向国际贸易的 AI 文档工作区",
    description: "面向国际交易的专业文档平台，提供结构化模板、AI 辅助起草以及商业、航运和贸易融资工作流。",
  },
  ru: {
    html: "ru",
    ogLocale: "ru_RU",
    title: "TradeDocs AI Desk — AI-платформа для документов международной торговли",
    description: "Профессиональные документы для международных сделок: структурированные шаблоны, AI-подготовка и единое рабочее пространство.",
  },
};

function applyLocaleToDocument(lang) {
  if (typeof document === "undefined") return;
  const meta = META[lang] || META.en;
  try {
    document.documentElement.setAttribute("lang", meta.html);
    if (meta.title) document.title = meta.title;
    const setMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content", meta.description);
    setMeta('meta[property="og:locale"]', "content", meta.ogLocale);
    setMeta('meta[property="og:title"]', "content", meta.title);
    setMeta('meta[property="og:description"]', "content", meta.description);
    setMeta('meta[name="twitter:title"]', "content", meta.title);
    setMeta('meta[name="twitter:description"]', "content", meta.description);
    // Also swap og:image to the localized preview when it is not the default.
    const localizedImg = `https://tradedocs.online/og-image.${lang}.png`;
    const defaultImg = "https://tradedocs.online/og-image.png";
    const imgUrl = ["it", "de"].includes(lang) ? localizedImg : defaultImg;
    setMeta('meta[property="og:image"]', "content", imgUrl);
    setMeta('meta[property="og:image:secure_url"]', "content", imgUrl);
    setMeta('meta[name="twitter:image"]', "content", imgUrl);
  } catch (e) { /* no-op */ }
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    // URL ?lang= wins over stored preference (so shared links land correctly).
    if (typeof window !== "undefined") {
      try {
        const q = new URLSearchParams(window.location.search).get("lang");
        if (q && SUPPORTED_LANGS.includes(q.toLowerCase())) return q.toLowerCase();
      } catch (e) { /* no-op */ }
    }
    const stored = localStorage.getItem("td_lang");
    if (SUPPORTED_LANGS.includes(stored)) return stored;
    return detectBrowserLang();
  });
  useEffect(() => {
    localStorage.setItem("td_lang", lang);
    applyLocaleToDocument(lang);
  }, [lang]);
  const t = useCallback((key) => DICT[lang]?.[key] ?? DICT.en[key] ?? key, [lang]);
  const value = useMemo(() => ({ lang, setLang, t }), [lang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
